import React, { useState, useEffect, useMemo, useReducer, useRef, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getMissingCompanyFields } from '../utils/companyData';
import { adresFirmy, liniaFirmyKlienta } from '../utils/contract';
import { useTranslation } from '../translations';
import { API_BASE } from '../config';
import { sanitizeContractHtml } from '../utils/sanitizeContractHtml';
import SectionEditor from '../components/contract/SectionEditor';
import PaperSheet, { pageCountLabel } from '../components/document/PaperSheet';
import {
  StatusStrip, StatusText, HistoryBox, HistoryHead, VersionCard, VersionDate, SheetNote,
  PublishButton, SmallButton, ModalOverlay, ModalBox, ModalTitle, ModalText, ModalActions,
  PdfModalBox, PdfModalHead, PdfDownloadLink, PdfFrame, PdfCloseButton,
  Container, TopBar, TitleBlock, PageTitle, TopActions, Intro, Notices,
  MainGrid, Panel, PreviewPanel, PanelHead, PanelTitle, PanelTools, PreviewFrame,
} from '../components/document/builderChrome';
import { useConfirm } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { zarejestrujZapisPrzedWyjsciem, trwaWylogowanie, kontoWKarcie } from '../utils/zapisPrzedWyjsciem';
import Banner from '../components/ui/Banner';
import { PanelContainer, Badge, ToolbarButton as Button, VariableChip as VariableButton } from '../ui/common';
import { TrashIcon, SaveIcon, UploadIcon, EyeIcon, DownloadIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { useKontoWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// ── IKONY SVG ──────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7v6h6"/>
    <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>
  </svg>
);

const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 7v6h-6"/>
    <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"/>
  </svg>
);

const MoveUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const MoveDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const AlignLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="17" y1="10" x2="3" y2="10"/>
    <line x1="21" y1="6" x2="3" y2="6"/>
    <line x1="21" y1="14" x2="3" y2="14"/>
    <line x1="17" y1="18" x2="3" y2="18"/>
  </svg>
);

const AlignCenterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="10" x2="5" y2="10"/>
    <line x1="21" y1="6" x2="3" y2="6"/>
    <line x1="21" y1="14" x2="3" y2="14"/>
    <line x1="19" y1="18" x2="5" y2="18"/>
  </svg>
);

const AlignRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="21" y1="10" x2="7" y2="10"/>
    <line x1="21" y1="6" x2="3" y2="6"/>
    <line x1="21" y1="14" x2="3" y2="14"/>
    <line x1="21" y1="18" x2="7" y2="18"/>
  </svg>
);

// ── STYLED COMPONENTS ──────────────────────────────────────────────────
// ── STYLED COMPONENTS ──────────────────────────────────────────────────
/**
 * REDESIGN 2026-08-21 — czytelnosc strony dla klienta koncowego.
 *
 * Strona byla zbudowana z wlasnych prymitywow (wlasny `Container`, `Title`, `Button`,
 * `AlertBox`, `SuccessBox`, wlasny baner statusu), wiec wygladala inaczej niz reszta panelu.
 * Teraz korzysta z `ui/common.js` i `components/ui/Banner.js` — tych samych, co pozostale
 * ekrany. To, co zostaje tutaj, to WYLACZNIE rzeczy specyficzne dla konstruktora
 * (kartka podgladu, karta sekcji, pigulki pol), zbudowane na tokenach z `index.css`.
 *
 * Cztery decyzje ukladu, ktore zmieniaja odbior strony:
 *
 *  1. PASEK AKCJI JEST PRZYKLEJONY U GORY. Wczesniej „Zapisz szkic" i „Publikuj" lezaly
 *     na SAMYM DOLE, pod cala lista sekcji — przy dziesieciu sekcjach trzeba bylo przewinac
 *     caly dokument, zeby cokolwiek zapisac.
 *  2. PODGLAD TOWARZYSZY PRZEWIJANIU (sticky w prawej kolumnie). Uklad „edytuj po lewej,
 *     sprawdzaj po prawej" dzialal dotad tylko na poczatku listy sekcji.
 *  3. KARTKA ZAMIAST RAMKI. Podglad wyglada jak kartka na biurku (bialy arkusz z cieniem
 *     w szarej ramie), bo to jest umowa, ktora klient za chwile wydrukuje.
 *  4. JEDEN AKCENT KOLORU. Do tej pory kazdy przycisk mial pelne tlo (niebieskie albo szare),
 *     wiec „Publikuj" nie roznil sie waga od „Pokaz wszystkie pola", a lista pol do wstawienia
 *     byla sciana niebieskich prostokatow. Teraz tlo ma tylko akcja glowna.
 */

// ── Szybki start / import ───────────────────────────────────────────────────
const StartCard = styled.div`
  padding: var(--sp-4);
  margin-bottom: var(--sp-4);
  border-radius: var(--r-md);
  border: 1px solid ${(p) => (p.$highlight ? 'var(--info-border)' : 'var(--input-border)')};
  background: ${(p) => (p.$highlight ? 'var(--info-bg)' : 'var(--panel-bg)')};
`;

const StartTitle = styled.div`
  margin-bottom: var(--sp-1);
  font-size: var(--fs-sm);
  font-weight: 700;
  color: var(--text);
`;

const StartHint = styled.div`
  margin-bottom: var(--sp-3);
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

const ChipRow = styled.div`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
  align-items: center;
`;

const ToolChip = styled.button`
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-pill);
  border: 1px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--info-bg)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'var(--info)' : 'var(--text)')};
  font-size: var(--fs-xs);
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);

  &:hover:not(:disabled) { border-color: var(--primary); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const ToolLabel = styled.span`
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

/* Rozwijacz („Masz juz umowe?", „Dostepne pola") — jeden wyglad zamiast dwoch wlasnych
   przyciskow ze strzalka wpisana jako znak. */
const Disclosure = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;

  &:hover { color: var(--text); }

  svg {
    transition: transform var(--dur) var(--ease);
    ${(p) => (p.$open ? 'transform: rotate(180deg);' : '')}
  }
`;

// ── Kroki edycji ────────────────────────────────────────────────────────────
const Stepper = styled.div`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-3);
`;

const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-1) var(--sp-3) var(--sp-1) var(--sp-1);
  border-radius: var(--r-pill);
  border: 1px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--info-bg)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'var(--info)' : 'var(--text-muted)')};
  font-family: inherit;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);

  &:hover:not(:disabled) { border-color: var(--primary); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

/* Numer kroku zamiast kropki/ptaszka wielkosci 14px: kropka „odwiedzony" i ptaszek
   „zmieniony" wygladaly niemal tak samo, wiec nie niosly juz zadnej informacji.
   Teraz numer mowi, ktory to krok, a kolor tla — czy zostal zmieniony. */
const StepNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  background: ${(p) => (p.$done ? 'var(--success)' : p.$active ? 'var(--primary)' : 'var(--input-border)')};
  color: ${(p) => (p.$done || p.$active ? 'var(--primary-contrast)' : 'var(--text-muted)')};
`;

/* Tryb tylko do odczytu blokuje WYŁĄCZNIE edycję treści (sekcje i „Dodaj sekcję”). Kroki widoku
   prostego i przełącznik „Zaawansowane” to nawigacja, więc zostają czynne: dawniej fieldset
   obejmował cały panel i osoba bez uprawnień widziała tylko krok 1 (test odbiorczy 11.10.2026). */
const ZablokowanaEdycja = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const HelperText = styled.p`
  margin: 0 0 var(--sp-4) 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

// ── Karta sekcji ────────────────────────────────────────────────────────────
/* Sekcje byly dotad oddzielone samym `<hr>` o przezroczystosci 0.2, wiec przy piecu
   sekcjach nie bylo widac, gdzie konczy sie jedna, a zaczyna druga. */
const Section = styled.article`
  padding: var(--sp-4);
  margin-bottom: var(--sp-3);
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  &:hover { border-color: var(--border); }
  &:focus-within { border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-3);
`;

const SectionIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: var(--r-sm);
  background: var(--input-border);
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 700;
`;

const SectionTitle = styled.h3`
  margin: 0;
  min-width: 0;
  font-size: var(--fs-base);
  font-weight: 700;
  color: ${(p) => (p.$muted ? 'var(--text-muted)' : 'var(--text)')};
  font-style: ${(p) => (p.$muted ? 'italic' : 'normal')};
  overflow-wrap: anywhere;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  margin-left: auto;
`;

const SectionWarningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: 2px var(--sp-2);
  border-radius: var(--r-pill);
  background: var(--warning-bg);
  border: 1px solid var(--warning-border);
  color: var(--warning);
  font-size: var(--fs-xs);
  font-weight: 700;
`;

const DragHandle = styled.span`
  display: flex;
  align-items: center;
  padding: 2px;
  color: var(--text-muted);
  opacity: 0.55;
  cursor: grab;
  user-select: none;
  transition: opacity var(--dur) var(--ease);

  &:hover { opacity: 1; }
  &:active { cursor: grabbing; }
`;

// ── Formularze ──────────────────────────────────────────────────────────────
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const Input = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  font-size: var(--fs-base);
  font-family: inherit;
  color: var(--text);
  background: var(--input-bg);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  font-size: var(--fs-sm);
  font-family: 'Courier New', monospace;
  color: var(--text);
  background: var(--input-bg);
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;


/* Wyrownanie naglowka: trzy przelaczniki jednego ustawienia, wiec jeden element, a nie
   trzy osobne przyciski z pelnym tlem (wczesniej niewybrane byly szare, czyli wygladaly
   na aktywne akcje). */
const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  overflow: hidden;
`;

const SegButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px var(--sp-2);
  border: none;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'var(--info-bg)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--info)' : 'var(--text-muted)')};
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);

  & + & { border-left: 1px solid var(--input-border); }
  &:hover { color: var(--text); }
`;

// ── Podglad dokumentu ───────────────────────────────────────────────────────
const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-3);
`;

/**
 * TYPOGRAFIA dokumentu wewnątrz kartki. Samą kartkę (wymiary A4, marginesy druku, skalowanie
 * do szerokości kolumny, linie podziału na strony) rysuje `components/document/PaperSheet.js`
 * — tło, cień i padding NIE należą już tutaj, bo muszą odpowiadać milimetrom papieru,
 * a nie szerokości panelu.
 *
 * Kolor pisma jest STAŁY. Wcześniej było tu `color: var(--text)`, więc w trybie ciemnym
 * podgląd umowy renderował się prawie białym pismem na białej kartce — tekst był
 * nieczytelny dokładnie tam, gdzie strona ma go pokazywać.
 */
const PreviewContainer = styled.div`
  color: #1a1a1a;
  font-size: ${props => props.$fontSizePx || DEFAULT_PREVIEW_FONT_SIZE_PX}px;
  --preview-scale: ${props => (props.$fontSizePx || DEFAULT_PREVIEW_FONT_SIZE_PX) / DEFAULT_PREVIEW_FONT_SIZE_PX};
  line-height: 1.62;

  /* Krój jak w PDF (lib/contractHtml.js, body): Arial, a na serwerze Liberation Sans o tych samych
     wymiarach. Dawniej Calibri/Segoe UI, więc kartka łamała wiersze inaczej niż plik (11.10.2026). */
  * {
    font-family: Arial, 'Liberation Sans', Helvetica, sans-serif;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  /* Treść sekcji ma pismo dokumentu, a akapity odstępy jak w PDF (lib/contractHtml.js: p, pre).
     Dawniej treść miała 0.95em, a akapity domyślne 1em u góry i u dołu, więc kartka liczyła inną
     liczbę stron niż plik (testy odbiorcze 11.10.2026). */
  p, pre { margin: 0 0 0.55em; }
  p:last-child, pre:last-child { margin-bottom: 0; }

  /* Komórki tabeli łamią się jak w PDF, czyli całymi wyrazami. Łamanie w środku wyrazu ściskało
     wąskie kolumny („L p.”, „Rod zaj”), a PDF ich nie łamie (ponowny test odbiorczy 11.10.2026). */
  th, td, th *, td * {
    overflow-wrap: normal;
    word-break: normal;
  }
`;

// Nagłówek umowy w podglądzie (format='html'): duży, pogrubiony, domyślnie
// wyśrodkowany; wewnętrzny <p style="text-align"> nadpisuje wyrównanie.
// Wartości muszą odpowiadać `h1`/`.contract-heading` w `Login/backend/lib/contractHtml.js` —
// podgląd ma pokazywać dokument, a nie jego przybliżenie.
const PreviewHeading = styled.div`
  font-size: 1.55em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  margin: 0 0 10px;
  & p, & h1, & h2, & h3 { margin: 0; }
`;

// Kreska pod tytułem dokumentu — odpowiednik `.title-rule` z renderera PDF.
const PreviewTitleRule = styled.div`
  border-bottom: 1.5px solid #1a1a1a;
  margin: 0 0 20px;
`;

// Nagłówek sekcji — odpowiednik `h2` z renderera PDF.
const PreviewSectionTitle = styled.div`
  font-size: 1.06em;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin: 22px 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #dedede;

  &:first-child { margin-top: 0; }
`;

/* Tabela sprzętu — 1:1 z `table` w `Login/backend/lib/contractHtml.js`.
   Kolory są STAŁE: na białej kartce `var(--input-border)`/`var(--panel-bg)` dawały
   w trybie ciemnym czarne komórki nagłówka. Wyrównanie kolumn idzie przez `nth-child`,
   dokładnie jak w PDF (tam nagłówki `<th>` muszą zostać bez atrybutów). */
const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0 16px;
  font-size: 1em;
  font-variant-numeric: tabular-nums;

  th, td {
    border: 1px solid #cfcfcf;
    padding: ${props => Math.max(4, Math.round((props.$fontPx || 14) * 0.55))}px
             ${props => Math.max(6, Math.round((props.$fontPx || 14) * 0.55) + 2)}px;
    text-align: left;
    vertical-align: top;
    font-size: ${props => props.$fontPx || 14}px;
    color: #1a1a1a;
  }

  th {
    background: #f2f2f2;
    font-weight: 700;
    letter-spacing: 0.02em;
    border-bottom: 1.5px solid #9a9a9a;
  }

  tbody tr:nth-child(even) td { background: #fafafa; }
  tbody tr:hover td { background: inherit; }

  th:nth-child(1), td:nth-child(1),
  th:nth-child(4), td:nth-child(4),
  th:nth-child(5), td:nth-child(5),
  th:nth-child(7), td:nth-child(7) { text-align: center; }
  th:nth-child(6), td:nth-child(6),
  th:nth-child(8), td:nth-child(8) { text-align: right; white-space: nowrap; }
  td:nth-child(8) { font-weight: 600; }
`;

const SubBlock = styled.div`
  margin-top: var(--sp-4);
  padding-top: var(--sp-4);
  border-top: 1px solid var(--input-border);
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;

  & + & { margin-top: var(--sp-3); }
`;

const SettingHint = styled.span`
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

const VarGroup = styled.div`
  margin-bottom: var(--sp-3);

  &:last-child { margin-bottom: 0; }
`;

const VarGroupLabel = styled.div`
  margin-bottom: var(--sp-2);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const DangerGhostButton = styled(Button)`
  background: transparent;
  border-color: var(--danger-border);
  color: var(--danger);

  &:hover:not(:disabled) {
    background: var(--danger-bg);
    border-color: var(--danger);
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: var(--r-sm);
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);

  &:hover:not(:disabled) { background: var(--hover-bg); color: var(--text); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AutoSaveIndicator = styled.span`
  font-size: var(--fs-xs);
  color: ${props => props.error ? 'var(--warning)' : 'var(--text-muted)'};
  white-space: nowrap;
`;

// ── ZMIENNE DOSTĘPNE ──────────────────────────────────────────────────
// P8.1e (6/6): klucze tej mapy sa JEDNOCZESNIE identyfikatorami (kod siega po
// AVAILABLE_VARIABLES['Umowa']) i etykietami grup na ekranie. Dlatego kluczy NIE tlumaczymy —
// tlumaczy sie wylacznie ich wyswietlanie, przez mape nizej.
const VAR_CATEGORY_KEYS = {
  'Dane Klienta': 'contractBuilder.catClient',
  'Umowa': 'contractBuilder.catContract',
  'Sprzęt': 'contractBuilder.catEquipment',
  'Finanse': 'contractBuilder.catFinance',
  'Organizacja': 'contractBuilder.catOrganization',
};

const AVAILABLE_VARIABLES = {
  'Dane Klienta': [
    { code: '{{firstName}}', descKey: 'contractBuilder.varFirstName' },
    { code: '{{lastName}}', descKey: 'contractBuilder.varLastName' },
    { code: '{{email}}', descKey: 'contractBuilder.varEmail' },
    { code: '{{phone}}', descKey: 'contractBuilder.varPhone' },
    { code: '{{address}}', descKey: 'contractBuilder.varAddress' },
    { code: '{{idNumber}}', descKey: 'contractBuilder.varIdNumber' },
    { code: '{{clientCompanyName}}', descKey: 'contractBuilder.varClientCompanyName' },
    { code: '{{clientNip}}', descKey: 'contractBuilder.varClientNip' },
    // Cała linia „Firma: X, NIP: Y”; u klienta prywatnego linia znika z umowy (11.10.2026).
    { code: '{{clientCompanyLine}}', descKey: 'contractBuilder.varClientCompanyLine' }
  ],
  'Umowa': [
    { code: '{{startDate}}', descKey: 'contractBuilder.varStartDate' },
    { code: '{{endDate}}', descKey: 'contractBuilder.varEndDate' },
    { code: '{{days}}', descKey: 'contractBuilder.varDays' }
  ],
  'Sprzęt': [
    { code: '{{equipmentTable}}', descKey: 'contractBuilder.varEquipmentTable' }
  ],
  'Kwoty': [
    { code: '{{totalPrice}}', descKey: 'contractBuilder.varTotalPrice' },
    { code: '{{deposit}}', descKey: 'contractBuilder.varDeposit' },
    { code: '{{discount}}', desc: 'Rabat (PLN)' },
    { code: '{{advanceAmount}}', descKey: 'contractBuilder.varAdvance' },
    { code: '{{amountDue}}', descKey: 'contractBuilder.varAmountDue' }
  ],
  'Organizacja': [
    { code: '{{organizationName}}', descKey: 'contractBuilder.varOrgName' },
    { code: '{{organizationAddress}}', descKey: 'contractBuilder.varOrgAddress' },
    { code: '{{organizationPhone}}', descKey: 'contractBuilder.varOrgPhone' },
    { code: '{{organizationEmail}}', desc: 'Email organizacji' },
    { code: '{{organizationNIP}}', desc: 'NIP organizacji' },
    { code: '{{bankAccount}}', descKey: 'contractBuilder.varBankAccount' },
    { code: '{{bankName}}', descKey: 'contractBuilder.varBankName' },
    { code: '{{companyLogo}}', desc: 'Logo firmy (obrazek)' }
  ],
  'Notatki': [
    { code: '{{notes}}', desc: 'Dodatkowe notatki/uwagi' }
  ]
};

const BASIC_STEPS = [
  {
    id: 'intro-client',
    labelKey: 'contractBuilder.step1',
    helperKey: 'contractBuilder.step1Helper',
    matcher: (section) => {
      const title = (section.title || '').toLowerCase();
      return section.type === 'heading' || title.includes('klient');
    }
  },
  {
    id: 'equipment-money',
    labelKey: 'contractBuilder.step2',
    helperKey: 'contractBuilder.step2Helper',
    matcher: (section) => {
      const title = (section.title || '').toLowerCase();
      return title.includes('sprzęt') || title.includes('sprzet') || title.includes('finans') || title.includes('kwot');
    }
  },
  {
    id: 'signature-extra',
    label: 'Krok 3: Podpisy i dodatki',
    helperKey: 'contractBuilder.step3Helper',
    matcher: (section) => {
      const title = (section.title || '').toLowerCase();
      return title.includes('podpis');
    }
  }
];

const inferBasicStepId = (section) => {
  if (BASIC_STEPS[0].matcher(section)) return BASIC_STEPS[0].id;
  if (BASIC_STEPS[1].matcher(section)) return BASIC_STEPS[1].id;
  if (BASIC_STEPS[2].matcher(section)) return BASIC_STEPS[2].id;
  return BASIC_STEPS[2].id;
};

const withBasicStepId = (sections = []) => sections.map((section) => ({
  ...section,
  basicStepId: section.basicStepId || inferBasicStepId(section)
}));

// P8.1e (6/6): TA TREŚĆ ZOSTAJE PO POLSKU. To gotowe wzory UMOWY — dokumentu prawnego,
// który klient podpisuje. `applyPreset` wsypuje je do sekcji, sekcje zapisują się jako
// szablon organizacji, a z niego backend generuje PDF. Przetłumaczone zmieniałyby się razem
// z językiem PANELU, więc niemieckojęzyczny pracownik opublikowałby niemiecki wzór umowy
// dla polskich klientów — a to dokument o skutkach prawnych, nie napis na przycisku.
// Tytuły sekcji są dodatkowo DOPASOWYWANE przez `matcher` w BASIC_STEPS i przez
// `title.includes('sprzęt')` niżej, więc ich tłumaczenie zerwałoby też przypisanie
// sekcji do kroków kreatora.
const SECTION_PRESETS = {
  standard: [
    {
      title: 'Umowa Wypożyczenia Sprzętu',
      type: 'heading',
      content: 'Umowa Wypożyczenia Sprzętu',
      alignment: 'left'
    },
    {
      title: 'Dane Klienta',
      type: 'section',
      content: `Imię i nazwisko: {{firstName}} {{lastName}}
Email: {{email}}
Telefon: {{phone}}
Adres: {{address}}
Nr dowodu: {{idNumber}}
{{clientCompanyLine}}`,
      alignment: 'left'
    },
    {
      title: 'Sprzęt',
      type: 'section',
      content: 'Przedmiot umowy (sprzęt):\n{{equipmentTable}}\n\nOkres wypożyczenia: od {{startDate}} do {{endDate}} ({{days}} dni)',
      alignment: 'left'
    },
    {
      title: 'Warunki Finansowe',
      type: 'section',
      content: `Całkowita cena: {{totalPrice}} PLN
Rabat: {{discount}} PLN
Zaliczka: {{advanceAmount}} PLN
Do zapłaty: {{amountDue}} PLN
Kaucja: {{deposit}} PLN`,
      alignment: 'left'
    },
    {
      title: 'Podpisy',
      type: 'section',
      content: `__________________________       __________________________
Klient                          {{organizationName}}`,
      alignment: 'center'
    }
  ],
  withNotes: [
    {
      title: 'Umowa Wypożyczenia Sprzętu',
      type: 'heading',
      content: 'Umowa Wypożyczenia Sprzętu',
      alignment: 'left'
    },
    {
      title: 'Dane Klienta',
      type: 'section',
      content: `Imię i nazwisko: {{firstName}} {{lastName}}
Email: {{email}}
Telefon: {{phone}}
Adres: {{address}}
Nr dowodu: {{idNumber}}
{{clientCompanyLine}}`,
      alignment: 'left'
    },
    {
      title: 'Sprzęt',
      type: 'section',
      content: 'Przedmiot umowy (sprzęt):\n{{equipmentTable}}\n\nOkres wypożyczenia: od {{startDate}} do {{endDate}} ({{days}} dni)',
      alignment: 'left'
    },
    {
      title: 'Warunki Finansowe',
      type: 'section',
      content: `Całkowita cena: {{totalPrice}} PLN
Rabat: {{discount}} PLN
Zaliczka: {{advanceAmount}} PLN
Do zapłaty: {{amountDue}} PLN
Kaucja: {{deposit}} PLN`,
      alignment: 'left'
    },
    {
      title: 'Dodatkowe Ustalenia',
      type: 'section',
      content: `Uwagi dodatkowe:\n{{notes}}`,
      alignment: 'left'
    },
    {
      title: 'Podpisy',
      type: 'section',
      content: `__________________________       __________________________
Klient                          {{organizationName}}`,
      alignment: 'center'
    }
  ],
  noDeposit: [
    {
      title: 'Umowa Wypożyczenia Sprzętu',
      type: 'heading',
      content: 'Umowa Wypożyczenia Sprzętu',
      alignment: 'left'
    },
    {
      title: 'Dane Klienta',
      type: 'section',
      content: `Imię i nazwisko: {{firstName}} {{lastName}}
Email: {{email}}
Telefon: {{phone}}
Adres: {{address}}
Nr dowodu: {{idNumber}}
{{clientCompanyLine}}`,
      alignment: 'left'
    },
    {
      title: 'Sprzęt',
      type: 'section',
      content: 'Przedmiot umowy (sprzęt):\n{{equipmentTable}}\n\nOkres wypożyczenia: od {{startDate}} do {{endDate}} ({{days}} dni)',
      alignment: 'left'
    },
    {
      title: 'Warunki Finansowe',
      type: 'section',
      content: `Całkowita cena: {{totalPrice}} PLN
Rabat: {{discount}} PLN
Zaliczka: {{advanceAmount}} PLN
Do zapłaty: {{amountDue}} PLN
Kaucja: nie dotyczy`,
      alignment: 'left'
    },
    {
      title: 'Podpisy',
      type: 'section',
      content: `__________________________       __________________________
Klient                          {{organizationName}}`,
      alignment: 'center'
    }
  ]
};

// Scenariusze podglądu: każdy ma WŁASNĄ tabelę sprzętu zgodną z liczbami w tekście (ponowny test
// odbiorczy 11.10.2026: tabela zostawała z pierwszego scenariusza, więc „dłuższy najem” miał w tekście
// 14 dni i 1900 PLN, a w tabeli 7 dni i 1050 PLN). Liczby jak w umowie z konstruktora: totalPrice to
// cena PRZED rabatem, amountDue = totalPrice - discount - advanceAmount, dni liczone z obu końców.
const sztuka = (lp, rodzajSprzet, marka, rozmiar, cenaDzien, liczbaDni) => ({
  lp, rodzajSprzet, marka, rozmiar, ilosc: 1, cenaDzien, liczbaDni, cenaLaczna: cenaDzien * liczbaDni,
});
const PREVIEW_SCENARIOS = {
  standard: {
    labelKey: 'contractBuilder.scenarioStandard',
    data: {
      days: 7,
      totalPrice: 1050,
      discount: 50,
      advanceAmount: 200,
      amountDue: 800,
      deposit: 500,
      startDate: '2026-04-01',
      endDate: '2026-04-07',
      equipment: [sztuka(1, 'Narty', 'Atomic', '170 cm', 150, 7)]
    }
  },
  noDeposit: {
    labelKey: 'contractBuilder.scenarioNoDeposit',
    data: {
      days: 3,
      totalPrice: 450,
      discount: 0,
      advanceAmount: 150,
      amountDue: 300,
      deposit: 0,
      startDate: '2026-04-12',
      endDate: '2026-04-14',
      equipment: [sztuka(1, 'Narty', 'Atomic', '170 cm', 150, 3)]
    }
  },
  longTerm: {
    labelKey: 'contractBuilder.scenarioLongTerm',
    data: {
      days: 14,
      totalPrice: 2100,
      discount: 100,
      advanceAmount: 400,
      amountDue: 1600,
      deposit: 800,
      startDate: '2026-05-01',
      endDate: '2026-05-14',
      equipment: [
        sztuka(1, 'Narty', 'Atomic', '170 cm', 100, 14),
        sztuka(2, 'Buty narciarskie', 'Salomon', '27,5', 50, 14)
      ]
    }
  }
};

// Wartości przykładowe tak, jak wypisuje je prawdziwa umowa (routes/contracts.js, generate-contract):
// daty DD.MM.RRRR, kwoty z dwoma miejscami po kropce. Dawniej podgląd i „Podgląd PDF” pisały daty
// RRRR-MM-DD i kwoty bez groszy (ponowny test odbiorczy 11.10.2026).
const POLA_KWOT_PODGLADU = ['totalPrice', 'discount', 'advanceAmount', 'amountDue', 'deposit'];
const dataJakWUmowie = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v == null ? '' : v));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
};
const kwotaJakWUmowie = (v) => (
  v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? v : Number(v).toFixed(2)
);
export function danePodgladuJakWUmowie(dane) {
  const wynik = { ...dane, startDate: dataJakWUmowie(dane.startDate), endDate: dataJakWUmowie(dane.endDate) };
  POLA_KWOT_PODGLADU.forEach((klucz) => {
    if (klucz in dane) wynik[klucz] = kwotaJakWUmowie(dane[klucz]);
  });
  if (Array.isArray(dane.equipment)) {
    wynik.equipment = dane.equipment.map((eq) => ({
      ...eq,
      cenaDzien: kwotaJakWUmowie(eq.cenaDzien),
      cenaLaczna: kwotaJakWUmowie(eq.cenaLaczna),
    }));
  }
  return wynik;
}

// Wartość pola wstawiana w treść sekcji z edytora (HTML): jako TEKST, nie znaczniki. Dawniej
// „<uwaga>” w notatkach albo nazwie klienta znikało, bo sanitizer zdejmował je jako nieznany
// znacznik (ponowny test odbiorczy 11.10.2026). Sanityzacja całości zostaje bez zmian.
const escapeHtmlWartosci = (v) => String(v)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Wzorce HTML treści sekcji, lustro lib/contractHtml.js. Wszystkie LINIOWE (przegląd
// bezpieczeństwa 12.10.2026): dawne `[^>]*` i `[\s\S]*?` do `</p>` przy tekście z setkami `<`
// albo `<p` bez domknięcia sprawdzały od każdego z nich całą resztę tekstu (czas kwadratowy),
// a kartka liczy się przy każdym naciśnięciu klawisza. Teraz znacznik kończy się na najbliższym
// `<`, a akapit na najbliższym `<p` albo `</p`.
const AKAPIT_HTML = /<p\b[^<>]*>((?:(?!<\/?p\b)[\s\S])*?)<\/p>/gi;
const ZNACZNIK_HTML = /<[^<>]*>/g;
// Grupa przechwytująca zostawia bloki w wyniku split: bloki są ZAWSZE pod nieparzystymi
// indeksami, a tekst pod parzystymi.
const BLOKI_SEKCJI = /(<div[^<>]*data-equipment-table[^<>]*>\s*<\/div>|<div[^<>]*data-company-logo[^<>]*>\s*<\/div>)/i;

// Czy blok nagłówka niesie JAKIKOLWIEK tekst. Domyślny preset trzyma nazwę bloku w `title`,
// a `content` zostawia PUSTE — dopóki użytkownik nic nie wpisze, tytuł dokumentu jest pusty
// i kreska pod nim wisiałaby nad niczym. Lustro `headingHasText` z lib/contractHtml.js.
export const headingHasText = (content) => String(content || '')
  .replace(ZNACZNIK_HTML, ' ')
  .replace(/&nbsp;/gi, ' ')
  .trim().length > 0;

// Lustro lib/contractHtml.js#usunPusteLinieZmiennych: linia (tekst) albo akapit <p> (edytor),
// który niósł zmienną i po podstawieniu jest pusty, znika z umowy. Bez tego kartka pokazywała
// pustą linię, której PDF nie ma (np. {{clientCompanyLine}} u klienta prywatnego albo puste pole
// danych firmy). Nieznana zmienna zostaje na kartce jako ostrzegawczy chip, więc linia z nią
// nie znika (scalenie poprawek 11.10.2026). `podstaw(tekst, { html })` podstawia wartości pól.
const ZMIENNA_W_TRESCI = /{{\s*\w+\s*}}/;
const BLOK_W_TRESCI = /{{\s*(equipmentTable|companyLogo)\s*}}|data-equipment-table|data-company-logo/i;
export function usunPusteLinieZmiennych(tresc, format, podstaw) {
  const tekst = String(tresc || '');
  if (format === 'html') {
    return tekst.replace(AKAPIT_HTML, (cala, wnetrze) => {
      if (!ZMIENNA_W_TRESCI.test(wnetrze) || BLOK_W_TRESCI.test(wnetrze)) return cala;
      const widoczne = podstaw(wnetrze, { html: true })
        .replace(ZNACZNIK_HTML, '').replace(/&nbsp;/gi, ' ').trim();
      return widoczne ? cala : '';
    });
  }
  return tekst.split('\n')
    .filter((linia) => !(ZMIENNA_W_TRESCI.test(linia) && !BLOK_W_TRESCI.test(linia) && !podstaw(linia).trim()))
    .join('\n');
}

// Treść sekcji z edytora po podstawieniu, pocięta na tekst i chronione bloki (tabela sprzętu,
// logo). `blok` to rodzaj bloku albo null dla tekstu. O tym, czy kawałek jest blokiem, decyduje
// WYŁĄCZNIE jego miejsce w wyniku split (nieparzysty indeks), nie treść: dawniej wartość pola
// z napisem „data-equipment-table” albo „data-company-logo” (np. notatka z widgetu, nazwa firmy)
// zamieniała cały kawałek tekstu w tabelę sprzętu albo logo, więc tekst znikał z kartki
// (przegląd bezpieczeństwa 12.10.2026, lustro serwera).
export function podzielNaBloki(html) {
  return String(html || '').split(BLOKI_SEKCJI).map((tekst, i) => {
    if (i % 2 === 0) return { blok: null, tekst };
    return { blok: /data-equipment-table/i.test(tekst) ? 'equipment' : 'logo', tekst };
  });
}

const DEFAULT_PREVIEW_FONT_SIZE_PX = 14;
const DEFAULT_LOGO_HEIGHT_PX = 70;
const MAX_LOGO_SIZE_BYTES = 1024 * 1024;
const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
// Numeryczne rozmiary pisma w px (jak w edytorze) — dla dokumentu i tabeli sprzętu.
const DOCUMENT_FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24];
const clampPreviewFontSize = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_PREVIEW_FONT_SIZE_PX;
  }
  return Math.min(24, Math.max(6, Math.round(parsed)));
};

// Niezależny rozmiar czcionki tabeli sprzętu — absolutny w px.
const DEFAULT_TABLE_FONT_PX = 14;
// Najwyżej 16 px, jak w lib/contractHtml.js: tabela w większym piśmie bywa szersza od obszaru
// druku, a Chromium zmniejszał wtedy w PDF całą stronę (ponowny test odbiorczy 11.10.2026).
const TABLE_FONT_SIZES = [9, 10, 11, 12, 13, 14, 16];
const clampTableFontPx = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_TABLE_FONT_PX;
  return Math.min(16, Math.max(8, Math.round(parsed)));
};

// Pismo treści i tabeli zapisane w previewData szkicu albo wersji: te same zasady przy
// wczytaniu edytora, przy wersji opublikowanej i przy przywracaniu wersji z historii.
const pismoZPreviewData = (pd) => {
  const d = pd && typeof pd === 'object' ? pd : {};
  return {
    font: d.contractFontSizePx != null ? clampPreviewFontSize(d.contractFontSizePx) : DEFAULT_PREVIEW_FONT_SIZE_PX,
    table: d.contractTableFontPx != null ? clampTableFontPx(d.contractTableFontPx) : DEFAULT_TABLE_FONT_PX,
  };
};

const clampLogoHeight = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_LOGO_HEIGHT_PX;
  }
  return Math.min(140, Math.max(40, Math.round(parsed)));
};

const normalizeLogoAlign = (value) => {
  if (value === 'left' || value === 'right' || value === 'center') {
    return value;
  }
  return 'center';
};

// Pola podglądu pochodzące WYŁĄCZNIE z ustawień firmy (GET /api/contract-settings).
// Nie wolno ich brać z zapisanego previewData (starsze szablony mają tam zapisane fikcyjne
// "Wypo-Narty Sp. z o.o." z czasów, gdy podgląd fabrykował dane firmy) ani wypełniać
// zmyślonymi wartościami — braki sygnalizuje baner "Niekompletne dane firmy".
const COMPANY_PREVIEW_FIELDS = [
  'organizationName', 'organizationAddress', 'organizationPhone', 'organizationEmail',
  'organizationNIP', 'bankAccount', 'bankName',
  'organizationLogoUrl', 'organizationLogoAlign', 'organizationLogoHeightPx'
];

const stripCompanyPreviewFields = (data) => {
  const copy = { ...data };
  COMPANY_PREVIEW_FIELDS.forEach((key) => delete copy[key]);
  return copy;
};

// ── REDUCER DO ZARZĄDZANIA STANEM Z HISTORIĄ ──────────────────────────
const createInitialState = () => ([
  {
    sections: [
      {
        id: 1,
        title: 'Umowa Wypożyczenia Sprzętu',
        type: 'heading',
        content: 'Umowa Wypożyczenia Sprzętu',
        basicStepId: 'intro-client'
      },
      {
        id: 2,
        title: 'Dane Klienta',
        type: 'section',
        // Linia firmy klienta jak w gotowych wzorach: u klienta prywatnego znika z umowy.
        content: `Imię i nazwisko: {{firstName}} {{lastName}}
Email: {{email}}
Telefon: {{phone}}
Adres: {{address}}
Nr dowodu: {{idNumber}}
{{clientCompanyLine}}`,
        alignment: 'left',
        basicStepId: 'intro-client'
      },
      {
        id: 3,
        title: 'Sprzęt',
        type: 'section',
        content: 'Przedmiot umowy (sprzęt):\n{{equipmentTable}}\n\nWyszczególnienie: Sprzęt do wypożyczenia na okres od {{startDate}} do {{endDate}} ({{days}} dni)',
        alignment: 'left',
        basicStepId: 'equipment-money'
      },
      {
        id: 4,
        title: 'Warunki Finansowe',
        type: 'section',
        content: `Całkowita cena: {{totalPrice}} PLN
Rabat: {{discount}} PLN
Zaliczka: {{advanceAmount}} PLN
Do zapłaty: {{amountDue}} PLN
Kaucja: {{deposit}} PLN`,
  alignment: 'left',
  basicStepId: 'equipment-money'
      },
      {
        id: 5,
        title: 'Podpisy',
        type: 'section',
        content: `__________________________       __________________________
Klient                          {{organizationName}}`,
  alignment: 'center',
  basicStepId: 'signature-extra'
      }
    ],
    loading: false,
    saved: true,
    message: ''
  }
]);

// Migawka tego, co zapisuje autozapis: sekcje, rozmiary pisma treści i tabeli oraz scenariusz
// podglądu. Scenariusz zapisuje się z szablonem (previewData.previewScenario), ale nie był
// w migawce, więc sama jego zmiana nie uruchamiała autozapisu ani zapisu przy wyjściu i ginęła
// po przeładowaniu (przegląd kodu 12.10.2026).
// Treść odpowiedzi błędu podglądu PDF jako obiekt. Podgląd prosi o `responseType: 'blob'`, więc
// odpowiedź 400/413 (limity treści, umowa za duża) też przychodzi jako Blob, a samo `err.message`
// to „Request failed with status code 413” zamiast zdania z serwera (scalenie poprawek 12.10.2026).
export async function daneBleduPdf(data) {
  if (!data) return null;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const tekst = typeof data.text === 'function'
        ? await data.text()
        : await new Promise((ok, blad) => {
          const czytnik = new FileReader();
          czytnik.onload = () => ok(String(czytnik.result || ''));
          czytnik.onerror = () => blad(czytnik.error);
          czytnik.readAsText(data);
        });
      return JSON.parse(tekst);
    } catch (_) {
      return null;
    }
  }
  return typeof data === 'object' ? data : null;
}

function migawkaSzablonu(sections, fontPx, tableFontPx, scenariusz) {
  return JSON.stringify([sections, fontPx, tableFontPx, scenariusz]);
}

function contractReducer(state, action) {
  const { history, historyIndex } = state;
  const currentState = history[historyIndex];

  const pushToHistory = (newSections) => ({
    history: history.slice(0, historyIndex + 1).concat([
      {
        ...currentState,
        sections: newSections,
        saved: false
      }
    ]),
    historyIndex: historyIndex + 1
  });

  switch (action.type) {
    case 'UPDATE_SECTION': {
      const newSections = currentState.sections.map(s =>
        s.id === action.payload.id ? { ...s, ...action.payload.updates } : s
      );
      return { ...state, ...pushToHistory(newSections) };
    }
    case 'ADD_SECTION': {
      const newId = Math.max(...currentState.sections.map(s => s.id), 0) + 1;
      const stepId = action.payload?.basicStepId || 'signature-extra';
      const newSection = {
        id: newId,
        title: 'Nowa sekcja',
        type: 'section',
        content: '',
        alignment: 'left',
        basicStepId: stepId,
        isCustom: true
      };

      const stepOrder = BASIC_STEPS.map((step) => step.id);
      const currentStepIdx = Math.max(stepOrder.indexOf(stepId), 0);
      const lastInCurrentStepIdx = currentState.sections.reduce((acc, section, idx) => (
        (section.basicStepId || inferBasicStepId(section)) === stepId ? idx : acc
      ), -1);

      let insertIdx = action.payload?.append ? currentState.sections.length : lastInCurrentStepIdx + 1;

      if (lastInCurrentStepIdx === -1) {
        // Jeśli w kroku nie ma jeszcze sekcji, wstaw po ostatniej sekcji z wcześniejszych kroków.
        const previousStepIds = stepOrder.slice(0, currentStepIdx);
        const lastInPreviousStepsIdx = currentState.sections.reduce((acc, section, idx) => {
          const sectionStepId = section.basicStepId || inferBasicStepId(section);
          return previousStepIds.includes(sectionStepId) ? idx : acc;
        }, -1);
        insertIdx = lastInPreviousStepsIdx + 1;
      }

      const newSections = [
        ...currentState.sections.slice(0, insertIdx),
        newSection,
        ...currentState.sections.slice(insertIdx)
      ];
      return { ...state, ...pushToHistory(newSections) };
    }
    case 'REMOVE_SECTION': {
      const newSections = currentState.sections.filter(s => s.id !== action.payload);
      return { ...state, ...pushToHistory(newSections) };
    }
    case 'MOVE_SECTION_UP': {
      const idx = currentState.sections.findIndex(s => s.id === action.payload);
      if (idx > 0) {
        const newSections = [...currentState.sections];
        [newSections[idx], newSections[idx - 1]] = [newSections[idx - 1], newSections[idx]];
        return { ...state, ...pushToHistory(newSections) };
      }
      return state;
    }
    case 'MOVE_SECTION_DOWN': {
      const idx = currentState.sections.findIndex(s => s.id === action.payload);
      if (idx < currentState.sections.length - 1) {
        const newSections = [...currentState.sections];
        [newSections[idx], newSections[idx + 1]] = [newSections[idx + 1], newSections[idx]];
        return { ...state, ...pushToHistory(newSections) };
      }
      return state;
    }
    case 'UPDATE_SECTION_ALIGNMENT': {
      const newSections = currentState.sections.map(s =>
        s.id === action.payload.id ? { ...s, alignment: action.payload.alignment } : s
      );
      return { ...state, ...pushToHistory(newSections) };
    }
    case 'MOVE_SECTION_TO': {
      const { fromId, toId } = action.payload;
      const fromIdx = currentState.sections.findIndex(s => s.id === fromId);
      const toIdx = currentState.sections.findIndex(s => s.id === toId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return state;
      const newSections = [...currentState.sections];
      const [moved] = newSections.splice(fromIdx, 1);
      newSections.splice(toIdx, 0, moved);
      return { ...state, ...pushToHistory(newSections) };
    }
    case 'UNDO':
      if (historyIndex > 0) {
        return { ...state, historyIndex: historyIndex - 1 };
      }
      return state;
    case 'REDO':
      if (historyIndex < history.length - 1) {
        return { ...state, historyIndex: historyIndex + 1 };
      }
      return state;
    case 'SET_LOADING':
      return {
        ...state,
        history: state.history.map((item, idx) =>
          idx === state.historyIndex ? { ...item, loading: action.payload } : item
        )
      };
    // Ton komunikatu idzie razem z nim: dawniej wynikał z `saved`, więc błąd przy zapisanym
    // szablonie („Błąd usuwania…”) wyświetlał się na zielono (test odbiorczy 11.10.2026).
    case 'SET_SAVED':
      return {
        ...state,
        history: state.history.map((item, idx) =>
          idx === state.historyIndex ? { ...item, saved: true, message: action.payload || '', messageTone: 'success' } : item
        )
      };
    case 'SET_MESSAGE':
      return {
        ...state,
        history: state.history.map((item, idx) =>
          idx === state.historyIndex ? { ...item, message: action.payload, messageTone: action.tone || 'info' } : item
        )
      };
    case 'REPLACE_SECTIONS': {
      return { ...state, ...pushToHistory(withBasicStepId(action.payload || [])) };
    }
    // Szablon wczytany z serwera ZACZYNA historię: wcześniej trafiał do niej jako zmiana po
    // wzorze domyślnym, więc Ctrl+Z cofał do wzoru domyślnego, a autozapis zapisywał go jako
    // szkic firmy (test odbiorczy 11.10.2026).
    case 'LOAD_SECTIONS': {
      const sections = withBasicStepId(action.payload || []);
      return { ...state, history: [{ ...currentState, sections, saved: true }], historyIndex: 0 };
    }
    default:
      return state;
  }
}

// Raport importu treści umowy: pola, których import nie rozpoznał. Parametr pozycji nazywa się
// `tok`, nie `t`: przy nazwie `t` przesłaniał funkcję tłumaczeń, więc pole bez podpowiedzi
// wywoływało obiekt jak funkcję i React odmontowywał cały panel (biała strona, przepadały
// niezapisane zmiany). Pilnuje pages/__tests__/raportImportuUmowy.test.js.
export function ImportReportList({ report }) {
  const { t } = useTranslation();
  return (
    <div style={{ marginTop: 'var(--sp-3)' }}>
      <Banner tone="warning" title={t('contractBuilder.importPartial')}>
        <ul style={{ margin: 'var(--sp-2) 0 0 0', paddingLeft: 18 }}>
          {report.map((entry, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {t('contractBuilder.importSection').replace('{name}', entry.sectionTitle)}{' '}
              {entry.tokens.map((tok, j) => (
                <span key={j}>
                  {j > 0 && ', '}
                  <code>{tok.token}</code>
                  {/* Zmienne niosą opis jako klucz tłumaczenia (descKey), a raport czytał samo `desc`,
                      więc po pytaniu zostawało puste miejsce: „(czy chodziło o {{idNumber}}? )” (11.10.2026). */}
                  {tok.suggestion
                    ? <> ({t('contractBuilder.importDidYouMean')} <code>{tok.suggestion.code}</code>
                      {(tok.suggestion.descKey || tok.suggestion.desc)
                        ? <>, „{tok.suggestion.descKey ? t(tok.suggestion.descKey) : tok.suggestion.desc}”</>
                        : null}?)</>
                    : <> {t('contractBuilder.pickFieldBelow')}</>}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </Banner>
    </div>
  );
}

// Zapis przy wyjściu ze strony, który jeszcze trwa. Szybki powrót do konstruktora czytał szablon
// sprzed tego zapisu, czyli starą rewizję, więc pierwszy zapis po powrocie dostawał 409 jak z innej
// karty (przegląd kodu 12.10.2026). Wejście czeka na niego najwyżej kilka sekund.
let zapisPrzyWyjsciuWToku = null;
const LIMIT_CZEKANIA_NA_ZAPIS_PRZY_WYJSCIU_MS = 5000;

// ── GŁÓWNY KOMPONENT ──────────────────────────────────────────────────
export default function ContractTemplateBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [state, dispatch] = useReducer(contractReducer, { history: createInitialState(), historyIndex: 0 });
  const currentState = state.history[state.historyIndex];
  const [showAllVariablesBySection, setShowAllVariablesBySection] = useState({});
  const [editorMode, setEditorMode] = useState('basic');
  const [basicStep, setBasicStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState(() => new Set([0]));
  const [activeScenario, setActiveScenario] = useState('standard');
  // Żaden wzór nie jest „wybrany”, dopóki ktoś go nie kliknie: dawniej podświetlony był „Standard”
  // także przy wczytanym własnym szablonie i przy szablonie startowym, który nim nie jest (11.10.2026).
  const [activePreset, setActivePreset] = useState(null);
  // Punkt odniesienia dla znacznika „zmieniony” przy krokach widoku prostego: treść z chwili
  // wejścia (wczytany szablon albo szablon startowy), wybranego wzoru albo importu. Dawniej był nim
  // zawsze wzór „Standard”, więc na świeżym szablonie krok 2 miał ptaszek, choć nikt nic nie
  // zmienił, a przy własnym szablonie ptaszki stały przy wszystkich krokach (11.10.2026).
  const [bazaKrokow, setBazaKrokow] = useState(() => createInitialState()[0].sections);
  const [templateStatus, setTemplateStatus] = useState('empty');
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [deletingVersion, setDeletingVersion] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // Błąd publikacji pokazany W OKNIE: dawniej okno zamykało się przed odpowiedzią serwera, a błąd
  // trafiał do banera nad edytorem, którego przy przewiniętej stronie nie widać (11.10.2026).
  const [publishError, setPublishError] = useState(null);
  const [importText, setImportText] = useState('');
  const [importWarning, setImportWarning] = useState('');
  const [importReport, setImportReport] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // U.3: fetch ustawień firmy zakończony błędem (≠ "ustawienia niekompletne") — osobny baner
  const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState(null);
  const lastSavedSnapshotRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  // Szablon nie wczytał się z serwera: edytor stoi na wzorze domyślnym, więc NIE wolno niczego
  // zapisywać, bo nadpisałoby to szkic firmy (test odbiorczy 11.10.2026).
  const [templateLoadFailed, setTemplateLoadFailed] = useState(false);
  // Szablon zmieniono w innej karcie albo na innym komputerze (serwer: 409 template_changed). Jak
  // przy nieudanym wczytaniu: baner i żadnego zapisu, żeby nie nadpisać cudzych zmian (11.10.2026).
  const [templateConflict, setTemplateConflict] = useState(false);
  // Rewizja szablonu z GET; null = serwer jej nie zna, zapis bez kontroli jak dotąd.
  const revisionRef = useRef(null);
  // Zapisy idą po kolei: autozapis i „Zapisz szkic” w tej samej karcie nie mogą wysłać tej samej
  // rewizji naraz, bo drugi dostałby 409 jak z innej karty.
  const kolejkaZapisuRef = useRef(Promise.resolve());
  // Pracownik bez uprawnień administratora: konstruktor tylko do odczytu (decyzja Piotrka
  // 11.10.2026). Dawniej mógł edytować cały szablon, a odmowę dostawał dopiero przy zapisie,
  // więc praca przepadała. Flaga z localStorage jak w innych ekranach; zapis i tak sprawdza serwer.
  // F.11: konto w przerwie też tylko do odczytu (zapis szablonu odrzuca serwer).
  const { wPrzerwie, powod: powodOdczytu } = useKontoWPrzerwie();
  const tylkoOdczyt = wPrzerwie || !(localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1');
  // Konto z chwili otwarcia strony. Po zalogowaniu na inne konto w innej karcie ta karta nie
  // zapisuje niczego (autozapis, przycisk, wyjście, wylogowanie): zapis poszedłby z sesją nowego
  // konta, czyli do szablonu jego firmy (przegląd bezpieczeństwa 12.10.2026).
  const [kontoPrzyWejsciu] = useState(kontoWKarcie);
  const kontoZmienione = () => kontoWKarcie() !== kontoPrzyWejsciu;
  // Po konflikcie (409) edytor jest tylko do odczytu jak dla pracownika: dawniej przyjmował dalsze
  // zmiany, których nie dało się już zapisać, i nie ostrzegał przy zamknięciu karty (przegląd kodu
  // 12.10.2026). Zmiana sprzed konfliktu nadal jest „niezapisana”, więc zamknięcie karty pyta.
  const edycjaZablokowana = tylkoOdczyt || templateConflict;
  // Szablon wczytany (albo wiadomo, że się nie wczytał). Wcześniej zapis nie może pójść: bez
  // rewizji z odczytu serwer przyjąłby go bez kontroli i nadpisał szkic firmy wzorem domyślnym.
  const [szablonWczytany, setSzablonWczytany] = useState(false);
  // Sekcje wersji opublikowanej (JSON, po withBasicStepId): odznaka statusu porównuje z nimi
  // edytor, a historia wersji dostaje je po publikacji jako poprzednią wersję.
  const [publishedSnapshot, setPublishedSnapshot] = useState(null);
  // Pismo wersji opublikowanej i data jej publikacji. Generator umów bierze pismo z
  // published.previewData, więc odznaka porównuje także je: szkic różny wyłącznie wielkością
  // pisma dostawał „Opublikowany” i „Nowe umowy będą używać tego szablonu” (test 11.10.2026).
  const [publishedInfo, setPublishedInfo] = useState(null); // { font, table, publishedAt }
  const [previewFontSizePx, setPreviewFontSizePx] = useState(DEFAULT_PREVIEW_FONT_SIZE_PX);
  const [previewTableFontPx, setPreviewTableFontPx] = useState(DEFAULT_TABLE_FONT_PX);
  const [draggedId, setDraggedId] = useState(null);
  // Sekcja jest przeciągalna TYLKO gdy trzymasz uchwyt (⠿) — inaczej drag&drop
  // przechwytuje zaznaczanie tekstu w edytorze sekcji.
  const [dragHandleActiveId, setDragHandleActiveId] = useState(null);
  const dragOverIdRef = useRef(null);
  const [showImport, setShowImport] = useState(false);
  const [showVariablesList, setShowVariablesList] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null); // blob URL podglądu PDF (modal)
  // Przycisk „Podgląd PDF”: po zamknięciu okna fokus wraca na niego (builderChrome, powrotFokusu).
  const pdfPrzyciskRef = useRef(null);
  // Liczba stron A4 policzona przez PaperSheet z wysokości treści — tylko do podpisu
  // pod kartką (dokładny podział zna dopiero render PDF).
  const [previewPages, setPreviewPages] = useState(1);

  // K.3 — instancje edytorów TipTap per sekcja (do wstawiania zmiennych w miejscu kursora)
  const sectionEditorsRef = useRef({});
  // Sekcja, w której ostatnio stał kursor: lista „Wszystkie dostępne pola” wstawia pole TAM,
  // a nie zawsze do pierwszej sekcji (test odbiorczy 11.10.2026).
  const lastFocusedSectionIdRef = useRef(null);
  const registerSectionEditor = useCallback((sectionId, editor) => {
    if (editor) {
      sectionEditorsRef.current[sectionId] = editor;
      editor.on('focus', () => { lastFocusedSectionIdRef.current = sectionId; });
    } else {
      delete sectionEditorsRef.current[sectionId];
      if (lastFocusedSectionIdRef.current === sectionId) lastFocusedSectionIdRef.current = null;
    }
  }, []);
  // Mapa name → przyjazna etykieta dla pigułek zmiennych ('firstName' → 'Imię klienta')
  const variableLabelMap = useMemo(() => {
    const map = {};
    Object.values(AVAILABLE_VARIABLES).flat().forEach((v) => {
      const name = String(v.code).replace(/[{}]/g, '');
      map[name] = v.descKey ? t(v.descKey) : (v.desc || v.code);
    });
    return map;
  }, [t]);
  const labelForVariable = useCallback(
    (name) => variableLabelMap[name] || `{{${name}}}`,
    [variableLabelMap]
  );
  
  // Obsługa Ctrl+Z / Ctrl+Y
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (edycjaZablokowana) return;
      // W zwykłych polach (tytuł sekcji, import tekstu) działa cofanie przeglądarki: dawniej skrót
      // cofał cały szablon, a nie wpisany znak (11.10.2026). Edytor treści sekcji (TipTap) nie ma
      // własnej historii, więc tam skrót nadal cofa zmianę szablonu.
      const cel = e.target;
      if (cel && typeof cel.closest === 'function' && cel.closest('input, textarea, select')) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [edycjaZablokowana]);

  useEffect(() => {
    let isMounted = true;

    const loadBuilderData = async () => {
      if (zapisPrzyWyjsciuWToku) {
        let zegar = null;
        await Promise.race([
          zapisPrzyWyjsciuWToku,
          new Promise((resolve) => { zegar = setTimeout(resolve, LIMIT_CZEKANIA_NA_ZAPIS_PRZY_WYJSCIU_MS); }),
        ]);
        clearTimeout(zegar);
        if (!isMounted) return;
      }
      // U.3: osobna obsługa obu żądań — awaria pobrania ustawień firmy nie może przejść
      // bez echa (podgląd nie fabrykuje już danych firmy, więc user musi wiedzieć,
      // DLACZEGO pola firmy są puste). Brak szablonu nadal nie blokuje edytora.
      const [templateResult, settingsResult] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/contract-template-sections`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/contract-settings`, { withCredentials: true })
      ]);
      if (!isMounted) return;

      if (templateResult.status === 'fulfilled') {
        const templateData = templateResult.value.data || {};
        revisionRef.current = Number.isInteger(templateData.revision) ? templateData.revision : null;

        const { font: loadedFont, table: loadedTableFont } = pismoZPreviewData(templateData.previewData);
        // Scenariusz zapisuje się razem z szablonem i wraca przy wejściu, a jego liczby i tabela
        // pochodzą ze stałej, nie z zapisu (stare szkice mają tam niespójne dane, 11.10.2026).
        const zapisanyScenariusz = (templateData.previewData && typeof templateData.previewData === 'object'
          && PREVIEW_SCENARIOS[templateData.previewData.previewScenario])
          ? templateData.previewData.previewScenario
          : 'standard';
        if (Array.isArray(templateData.sections) && templateData.sections.length > 0) {
          const loadedSections = withBasicStepId(templateData.sections);
          dispatch({ type: 'LOAD_SECTIONS', payload: loadedSections });
          setBazaKrokow(loadedSections);
          // Stan z serwera traktujemy jako zapisany — bez tego auto-save odpaliloby sie zaraz po wejsciu
          lastSavedSnapshotRef.current = migawkaSzablonu(loadedSections, loadedFont, loadedTableFont, zapisanyScenariusz);
        }
        if (templateData.published && Array.isArray(templateData.published.sections)) {
          setPublishedSnapshot(JSON.stringify(withBasicStepId(templateData.published.sections)));
          setPublishedInfo({
            ...pismoZPreviewData(templateData.published.previewData),
            publishedAt: templateData.published.publishedAt || null,
          });
        }

        setTemplateStatus(templateData.status || 'empty');
        setPublishedVersion(templateData.published?.versionNo || null);
        setTemplateVersions(templateData.versions || []);

        if (templateData.previewData && typeof templateData.previewData === 'object') {
          const savedFontSize = templateData.previewData.contractFontSizePx;
          if (savedFontSize !== undefined && savedFontSize !== null) {
            setPreviewFontSizePx(clampPreviewFontSize(savedFontSize));
          }
          const savedTableFontPx = templateData.previewData.contractTableFontPx;
          if (savedTableFontPx !== undefined && savedTableFontPx !== null) {
            setPreviewTableFontPx(clampTableFontPx(savedTableFontPx));
          }

          setActiveScenario(zapisanyScenariusz);
          setPreviewData((prev) => ({
            ...prev,
            ...stripCompanyPreviewFields(templateData.previewData),
            ...PREVIEW_SCENARIOS[zapisanyScenariusz].data
          }));
        }
      }

      if (templateResult.status === 'rejected') setTemplateLoadFailed(true);
      setSzablonWczytany(true);

      if (settingsResult.status === 'fulfilled') {
        const settings = settingsResult.value.data?.settings || null;
        setCompanySettings(settings);
        setSettingsLoaded(true);

        // Dane firmy w podglądzie pochodzą WYŁĄCZNIE stąd — puste pole zostaje puste,
        // dokładnie tak, jak wyjdzie w wygenerowanej umowie (bez zmyślonych wartości).
        const s = settings || {};
        setPreviewData((prev) => ({
          ...prev,
          organizationName: s.company_name || '',
          // Ulica, kod i miasto z Danych firmy, jak w umowie z serwera (11.10.2026).
          organizationAddress: adresFirmy(s),
          organizationPhone: s.company_phone || '',
          organizationEmail: s.company_email || '',
          organizationNIP: s.company_nip || '',
          bankAccount: s.company_bank_account || '',
          bankName: s.company_bank_name || '',
          organizationLogoUrl: s.company_logo || '',
          organizationLogoAlign: normalizeLogoAlign(s.company_logo_align),
          organizationLogoHeightPx: s.company_logo_height_px
            ? clampLogoHeight(s.company_logo_height_px)
            : DEFAULT_LOGO_HEIGHT_PX
        }));
      } else {
        setSettingsLoadFailed(true);
      }
    };

    loadBuilderData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Brakujace kluczowe dane firmy — pokazujemy banner dopiero po udanym zaladowaniu ustawien,
  // zeby nie straszyc przy chwilowej awarii sieci.
  const missingCompanyFields = useMemo(
    () => (settingsLoaded ? getMissingCompanyFields(companySettings) : []),
    [settingsLoaded, companySettings]
  );
  
  const [previewData, setPreviewData] = useState({
    firstName: 'Jan',
    lastName: 'Kowalski',
    email: 'jan.kowalski@example.com',
    phone: '+48 123 456 789',
    address: 'ul. Krakowska 1, 34-100 Zakopane',
    idNumber: 'ABC 123456',
    clientCompanyName: 'Kowalski Sport Sp. z o.o.',
    clientNip: '5260250274',
    clientCompanyLine: liniaFirmyKlienta('Kowalski Sport Sp. z o.o.', '5260250274'),
    startDate: '2026-04-01',
    endDate: '2026-04-08',
    days: 7,
    totalPrice: 1050,
    deposit: 500,
    discount: 50,
    advanceAmount: 200,
    amountDue: 800,
    // U.3: dane firmy startują PUSTE i są uzupełniane wyłącznie z GET /api/contract-settings —
    // podgląd nie fabrykuje danych firmy (fikcyjne "Wypo-Narty" maskowało braki w ustawieniach).
    organizationName: '',
    organizationAddress: '',
    organizationPhone: '',
    organizationEmail: '',
    organizationNIP: '',
    organizationLogoUrl: '',
    organizationLogoAlign: 'center',
    organizationLogoHeightPx: DEFAULT_LOGO_HEIGHT_PX,
    bankAccount: '',
    bankName: '',
    notes: 'Sprzęt oddać w stanie nie gorszym niż w momencie wypożyczenia.',
    equipment: [
      { lp: 1, rodzajSprzet: 'Narty', marka: 'Atomic', rozmiar: '170 cm', ilosc: 1, cenaDzien: 150, liczbaDni: 7, cenaLaczna: 1050 }
    ],
    ...PREVIEW_SCENARIOS.standard.data
  });
  // Dane podglądu w zapisie, jaki ma prawdziwa umowa (daty, kwoty) — dla kartki i „Podgląd PDF”.
  const daneDokumentu = useMemo(() => danePodgladuJakWUmowie(previewData), [previewData]);

  // ── FUNKCJE POMOCNICZE ──
  // `html` — treść z edytora: wartość wchodzi jako tekst (escapowana), patrz escapeHtmlWartosci.
  // 0 to kwota, nie brak: dawniej `String(value || '')` dawało „Rabat:  PLN” (11.10.2026).
  const replaceVariables = (text, { html = false } = {}) => {
    let result = text;
    Object.entries(daneDokumentu).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      const tekst = value === undefined || value === null ? '' : String(value);
      result = result.replace(regex, html ? escapeHtmlWartosci(tekst) : tekst);
    });
    return result;
  };

  // Renderuje tekst z podmienionym zmiennymi; nierozpoznane {{...}} zamienia na bursztynowy chip
  const renderTextWithChips = (text) => {
    const replaced = replaceVariables(String(text || ''));
    const parts = replaced.split(/({{[^}]*}})/g);
    if (parts.length === 1) return replaced;
    return parts.map((part, i) => {
      if (/^{{[^}]*}}$/.test(part)) {
        return (
          <span key={i} style={{
            display: 'inline-block',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning-border)',
            borderRadius: '4px',
            padding: '1px 6px',
            fontSize: '0.85em',
            color: 'var(--warning)',
            fontFamily: 'monospace',
            fontWeight: 600
          }}>{part}</span>
        );
      }
      return part;
    });
  };

  // Render treści sekcji w podglądzie (musi odpowiadać PDF z backendu).
  // format==='html' → treść z edytora RTE: podstaw zmienne, rozetnij na bloku tabeli
  // sprzętu, sanityzuj (sanitizeContractHtml — ten sam allowlist co serwer), wstrzyknij jako HTML. Inaczej → legacy (chipy + pre-wrap).
  const renderSectionContentWithEquipment = (section) => {
    const { id: sectionId, alignment } = section;
    const content = usunPusteLinieZmiennych(section.content, section.format, replaceVariables);

    if (section.format === 'html') {
      const replaced = replaceVariables(String(content || ''), { html: true });
      // Rozetnij na chronionych blokach: tabela sprzętu ORAZ logo.
      return (
        <div style={{ textAlign: alignment || 'left' }}>
          {podzielNaBloki(replaced).map(({ blok, tekst: tok }, idx) => {
            if (blok === 'equipment') {
              return <React.Fragment key={`${sectionId}-eq-${idx}`}>{renderEquipmentTable()}</React.Fragment>;
            }
            if (blok === 'logo') {
              const m = tok.match(/text-align:\s*(left|right|center)/i);
              return <React.Fragment key={`${sectionId}-logo-${idx}`}>{renderLogoPreview(m ? m[1].toLowerCase() : null)}</React.Fragment>;
            }
            return tok && tok.trim() ? (
              <div key={`${sectionId}-html-${idx}`} style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: sanitizeContractHtml(tok) }} />
            ) : null;
          })}
        </div>
      );
    }

    const source = String(content || '');
    const segments = source.split(/{{\s*equipmentTable\s*}}/g);
    const hasEquipmentToken = segments.length > 1;

    if (!hasEquipmentToken) {
      return (
        <div style={{ whiteSpace: 'pre-wrap', textAlign: alignment || 'left' }}>
          {renderTextWithChips(source)}
        </div>
      );
    }

    return (
      <div style={{ textAlign: alignment || 'left' }}>
        {segments.map((segment, idx) => (
          <React.Fragment key={`${sectionId}-segment-${idx}`}>
            {segment && (
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {renderTextWithChips(segment)}
              </div>
            )}
            {idx < segments.length - 1 && renderEquipmentTable()}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderPreview = () => {
    // Kreska idzie pod pierwszym NIEPUSTYM blokiem nagłówka = tytułem dokumentu, tak samo
    // jak w rendererze PDF (`titleRuleRendered` w lib/contractHtml.js).
    const firstHeadingId = currentState.sections
      .find((s) => s.type === 'heading' && headingHasText(s.content))?.id;

    return currentState.sections.map((section) => {
      if (section.type === 'heading') {
        const rule = section.id === firstHeadingId ? <PreviewTitleRule /> : null;

        if (section.format === 'html') {
          return (
            <React.Fragment key={section.id}>
              <PreviewHeading
                dangerouslySetInnerHTML={{ __html: sanitizeContractHtml(replaceVariables(String(section.content || ''), { html: true })) }}
              />
              {rule}
            </React.Fragment>
          );
        }
        const headingAlign = section.alignment || 'center';
        return (
          <React.Fragment key={section.id}>
            <PreviewHeading as="div" style={{ textAlign: headingAlign }}>
              {renderTextWithChips(section.content)}
            </PreviewHeading>
            {rule}
          </React.Fragment>
        );
      }

      if (section.type === 'section') {
        return (
          <div key={section.id} style={{ marginBottom: '16px' }}>
            {section.title && section.title.trim() && (
              <PreviewSectionTitle style={{ textAlign: section.titleAlignment || section.alignment || 'left' }}>
                {section.title}
              </PreviewSectionTitle>
            )}
            {renderSectionContentWithEquipment(section)}
          </div>
        );
      }

      return null;
    });
  };

  const renderEquipmentTable = () => {
    if (!daneDokumentu.equipment || daneDokumentu.equipment.length === 0) return null;

    // P8.1e (6/6): nagłówki tej tabeli są LUSTREM `lib/contractHtml.js` (tam stoi dokładnie
    // <th>Lp.</th><th>Rodzaj</th>…<th>Ilość</th>). Backend renderuje umowę po polsku
    // niezależnie od języka panelu, więc przetłumaczony podgląd pokazywałby dokument,
    // którego klient nigdy nie dostanie.
    return (
      <PreviewTable $fontPx={previewTableFontPx}>
        <thead>
          <tr>
            <th>Lp.</th>
            <th>Rodzaj</th>
            <th>Marka</th>
            <th>Rozmiar</th>
            <th>Ilość</th>
            <th>Cena/dzień</th>
            <th>Dni</th>
            <th>Razem</th>
          </tr>
        </thead>
        <tbody>
          {daneDokumentu.equipment.map((eq) => (
            <tr key={eq.lp}>
              <td>{eq.lp}</td>
              <td>{eq.rodzajSprzet}</td>
              <td>{eq.marka}</td>
              <td>{eq.rozmiar}</td>
              <td>{eq.ilosc}</td>
              <td>{eq.cenaDzien} PLN</td>
              <td>{eq.liczbaDni}</td>
              <td>{eq.cenaLaczna} PLN</td>
            </tr>
          ))}
        </tbody>
      </PreviewTable>
    );
  };

  // Logo firmy w miejscu wstawionego bloku (podgląd) — z danych firmy.
  // `align` z bloku (pasek edytora) nadpisuje globalne wyrównanie logo.
  const renderLogoPreview = (blockAlign) => {
    if (!previewData.organizationLogoUrl) return null;
    const align = (blockAlign === 'left' || blockAlign === 'right' || blockAlign === 'center')
      ? blockAlign
      : normalizeLogoAlign(previewData.organizationLogoAlign);
    return (
      <div style={{ display: 'flex', justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', margin: '12px 0' }}>
        <img
          src={previewData.organizationLogoUrl}
          alt="Logo organizacji"
          style={{ maxHeight: `${clampLogoHeight(previewData.organizationLogoHeightPx)}px`, maxWidth: '260px', objectFit: 'contain' }}
        />
      </div>
    );
  };

  // Wygasła sesja (401, a po wylogowaniu w innej karcie 403 csrf_invalid) dostaje zdanie zamiast
  // surowego kodu błędu z serwera (test odbiorczy 11.10.2026).
  const opisBleduZapisu = (err) => {
    const status = err?.response?.status;
    const kod = err?.response?.data?.error;
    if (status === 401 || (status === 403 && kod === 'csrf_invalid')) return t('contractBuilder.sessionExpired');
    return t('contractBuilder.errorPrefix').replace('{msg}', kod || err?.message || '');
  };

  // Zwraca { ok, error }: okno publikacji czeka na wynik i dopiero po sukcesie się zamyka.
  // `przyWyjsciu` — zapis przy wyjściu ze strony albo przed wylogowaniem: strony już nie widać,
  // więc błąd pokazuje komunikat ulotny (ToastProvider żyje w App), a nie baner.
  const zapiszSzablon = async (action = 'save-draft', { silent = false, przyWyjsciu = false } = {}) => {
    if (!szablonWczytany) return { ok: false };
    if (templateLoadFailed || templateConflict) {
      const komunikat = t(templateConflict ? 'contractBuilder.templateConflict' : 'contractBuilder.templateLoadFailed');
      // Wyjście ze strony po konflikcie: strony już nie widać, więc komunikat ulotny mówi, że
      // zmiany sprzed konfliktu nie trafiły na serwer.
      if (przyWyjsciu) toast.error(t('contractBuilder.exitSaveFailed').replace('{msg}', komunikat));
      else if (!silent) dispatch({ type: 'SET_MESSAGE', payload: komunikat, tone: 'danger' });
      return { ok: false, error: komunikat };
    }
    if (tylkoOdczyt) return { ok: false };
    if (kontoZmienione()) {
      const komunikat = t('contractBuilder.accountChanged');
      if (silent) setAutoSaveStatus('error');
      else dispatch({ type: 'SET_MESSAGE', payload: komunikat, tone: 'danger' });
      return { ok: false, error: komunikat };
    }
    if (!silent) setActionLoading(action);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const savedSnapshot = migawkaSzablonu(currentState.sections, previewFontSizePx, previewTableFontPx, activeScenario);
    const savedSections = JSON.stringify(currentState.sections);
    const savedFont = previewFontSizePx;
    const savedTableFont = previewTableFontPx;
    const silentStart = Date.now();
    try {
      const response = await axios.post(
        `${API_BASE}/api/contract-template-sections`,
        {
          sections: currentState.sections,
          // Bez pól firmy — te żyją w contract_settings; persystowanie ich tutaj
          // wskrzeszałoby nieaktualne kopie przy wczytaniu szablonu (U.3).
          previewData: {
            ...stripCompanyPreviewFields(previewData),
            contractFontSizePx: previewFontSizePx,
            contractTableFontPx: previewTableFontPx,
            previewScenario: activeScenario
          },
          action,
          ...(revisionRef.current !== null ? { revision: revisionRef.current } : {})
        },
        { withCredentials: true, headers: { 'X-CSRF-Token': getCsrfToken() } }
      );

      if (Number.isInteger(response?.data?.revision)) revisionRef.current = response.data.revision;
      const newStatus = response?.data?.status || (action === 'publish' ? 'published' : 'draft');
      setTemplateStatus(newStatus);
      const newVersion = response?.data?.publishedVersion || publishedVersion;
      if (action === 'publish' && newVersion && newVersion !== publishedVersion) {
        // Wpis poprzedniej wersji NIESIE jej sekcje: bez nich „Przywróć” w tej samej sesji
        // kończył się „Nie udało się wczytać wersji” (test odbiorczy 11.10.2026). Niesie też jej
        // pismo i PRAWDZIWĄ datę publikacji: dawniej dostawał chwilę przeniesienia do historii,
        // czyli datę publikacji nowej wersji, a przywrócenie brało samą treść (11.10.2026).
        const poprzednie = publishedSnapshot ? JSON.parse(publishedSnapshot) : null;
        const poprzedniaInfo = publishedInfo;
        setTemplateVersions((prev) => {
          if (publishedVersion) {
            return [{
              versionNo: publishedVersion,
              ...(poprzedniaInfo?.publishedAt ? { publishedAt: poprzedniaInfo.publishedAt } : {}),
              ...(poprzednie ? { sections: poprzednie } : {}),
              ...(poprzedniaInfo ? { previewData: { contractFontSizePx: poprzedniaInfo.font, contractTableFontPx: poprzedniaInfo.table } } : {}),
            }, ...prev].slice(0, 20);
          }
          return prev;
        });
      }
      if (action === 'publish') {
        setPublishedSnapshot(savedSections);
        setPublishedInfo({
          font: savedFont,
          table: savedTableFont,
          publishedAt: response?.data?.publishedAt || new Date().toISOString(),
        });
      }
      setPublishedVersion(newVersion);

      lastSavedSnapshotRef.current = savedSnapshot;

      if (silent) {
        // "Zapisywanie..." ma byc widoczne min. ~900ms, inaczej miga niezauwazalnie
        const remaining = Math.max(0, 900 - (Date.now() - silentStart));
        setTimeout(() => {
          setLastAutoSaveAt(new Date());
          setAutoSaveStatus('saved');
        }, remaining);
        dispatch({ type: 'SET_SAVED' });
      } else {
        setLastAutoSaveAt(new Date());
        setAutoSaveStatus('saved');
        dispatch({ type: 'SET_SAVED', payload: response?.data?.message || t('contractBuilder.saved') });
        setTimeout(() => {
          dispatch({ type: 'SET_MESSAGE', payload: '' });
        }, 3000);
      }
      return { ok: true };
    } catch (err) {
      const konflikt = err?.response?.status === 409 && err?.response?.data?.code === 'template_changed';
      if (konflikt) setTemplateConflict(true);
      const komunikat = konflikt ? t('contractBuilder.templateConflict') : opisBleduZapisu(err);
      if (silent) {
        // Cichy zapis nie spamuje komunikatami — wskaznik pokaze blad, reczny zapis pokaze szczegoly
        setAutoSaveStatus('error');
        if (przyWyjsciu) toast.error(t('contractBuilder.exitSaveFailed').replace('{msg}', komunikat));
      } else {
        dispatch({ type: 'SET_MESSAGE', payload: komunikat, tone: 'danger' });
        // Baner stoi nad edytorem i przy przewiniętej stronie go nie widać; przy publikacji błąd
        // pokazuje samo okno.
        if (action !== 'publish') toast.error(komunikat);
      }
      return { ok: false, error: komunikat };
    } finally {
      if (!silent) setActionLoading(null);
    }
  };
  const handleSaveTemplate = (action, opcje) => {
    const wynik = kolejkaZapisuRef.current.then(() => zapiszSzablon(action, opcje));
    kolejkaZapisuRef.current = wynik.catch(() => {});
    return wynik;
  };

  // Auto-save szkicu: debounce po ostatniej zmianie sekcji
  useEffect(() => {
    if (templateLoadFailed || templateConflict || tylkoOdczyt || !szablonWczytany) return;
    // Rozmiar pisma treści i tabeli jest częścią szablonu: bez niego w migawce zmiana
    // „Wielkość pisma” nigdy nie uruchamiała zapisu i ginęła po przeładowaniu.
    const snapshot = migawkaSzablonu(currentState.sections, previewFontSizePx, previewTableFontPx, activeScenario);
    if (lastSavedSnapshotRef.current === null) {
      // Pierwszy render — traktuj stan poczatkowy jako zapisany (preset domyslny nie wywoluje zapisu)
      lastSavedSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === lastSavedSnapshotRef.current) return;
    if (actionLoading !== null) return;

    autoSaveTimerRef.current = setTimeout(() => {
      setAutoSaveStatus('saving');
      handleSaveTemplate('save-draft', { silent: true });
    }, 7000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [currentState.sections, previewFontSizePx, previewTableFontPx, activeScenario, actionLoading, templateLoadFailed, templateConflict, tylkoOdczyt, szablonWczytany]);

  // Wyjście ze strony z niezapisaną zmianą (test odbiorczy 11.10.2026): autozapis czeka 7 s po
  // ostatniej zmianie, a sprzątanie efektu tylko kasowało zegar, więc zmiana sprzed wyjścia
  // przepadała bez śladu. Przejście w panelu zapisuje szkic od razu, a zamknięcie karty albo
  // przeładowanie pyta przeglądarka.
  // Sprawdzane W CHWILI zapytania (funkcja odświeżana przy każdym renderze), a nie z wartości
  // policzonej przy ostatnim renderze: po zapisie przed wylogowaniem render jeszcze nie nastąpił,
  // a przeglądarka pytała o opuszczenie strony z już zapisanymi zmianami (11.10.2026).
  const niezapisaneRef = useRef(() => false);
  // Konflikt NIE zdejmuje niezapisanych zmian: zmiana, której zapis dostał 409, nie jest na
  // serwerze, więc zamknięcie karty pyta, a wyjście w panelu mówi, że jej nie zapisano (12.10.2026).
  niezapisaneRef.current = () => szablonWczytany && !templateLoadFailed && !tylkoOdczyt
    && lastSavedSnapshotRef.current !== null
    && migawkaSzablonu(currentState.sections, previewFontSizePx, previewTableFontPx, activeScenario) !== lastSavedSnapshotRef.current;
  const zapiszRef = useRef(null);
  zapiszRef.current = handleSaveTemplate;
  const kontoZmienioneRef = useRef(kontoZmienione);
  kontoZmienioneRef.current = kontoZmienione;
  useEffect(() => {
    const ostrzez = (e) => {
      // Po wylogowaniu strona nie pyta o wyjście, także gdy zapis przed wylogowaniem się nie udał
      // (sprawdzenie 12.10.2026: bez sesji „Zostań” i tak nie pozwala niczego zapisać).
      if (trwaWylogowanie() || !niezapisaneRef.current()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const zapiszPrzyWyjsciu = () => (
      niezapisaneRef.current() && zapiszRef.current && !kontoZmienioneRef.current()
        ? zapiszRef.current('save-draft', { silent: true, przyWyjsciu: true })
        : undefined
    );
    window.addEventListener('beforeunload', ostrzez);
    // Wylogowanie (utils/wyloguj.js) zapisuje szkic, póki sesja jeszcze istnieje.
    const wyrejestruj = zarejestrujZapisPrzedWyjsciem(zapiszPrzyWyjsciu);
    return () => {
      window.removeEventListener('beforeunload', ostrzez);
      wyrejestruj();
      const zapis = zapiszPrzyWyjsciu();
      if (zapis && typeof zapis.then === 'function') {
        const wpis = Promise.resolve(zapis).catch(() => {}).then(() => {
          if (zapisPrzyWyjsciuWToku === wpis) zapisPrzyWyjsciuWToku = null;
        });
        zapisPrzyWyjsciuWToku = wpis;
      }
    };
  }, []);

  const handleImportFromText = async () => {
    if (!importText.trim()) {
      setImportWarning(t('contractBuilder.importEmpty'));
      return;
    }

    try {
      setImportLoading(true);
      setImportWarning('');

      const response = await axios.post(
        `${API_BASE}/api/contract-template-sections/import-text`,
        { importText },
        { withCredentials: true, headers: { 'X-CSRF-Token': getCsrfToken() } }
      );

      const importedSections = response?.data?.sections;
      if (!Array.isArray(importedSections) || importedSections.length === 0) {
        setImportWarning(t('contractBuilder.importNoSections'));
        return;
      }

      dispatch({ type: 'REPLACE_SECTIONS', payload: importedSections });
      setBazaKrokow(withBasicStepId(importedSections));

      // Raport per sekcja: ktore sekcje zawieraja nierozpoznane pola + sugestia najblizszej znanej zmiennej
      const report = importedSections
        .map((section) => {
          const tokens = getSectionUnknownVars(section);
          if (tokens.length === 0) return null;
          return {
            sectionTitle: section.title || t('contractBuilder.untitled'),
            tokens: tokens.map((token) => ({ token, suggestion: suggestKnownVariable(token) }))
          };
        })
        .filter(Boolean);

      if (report.length > 0) {
        setImportWarning('');
        setImportReport(report);
      } else {
        setImportReport(null);
        setImportWarning(t('contractBuilder.imported'));
      }

      if (editorMode === 'basic') {
        goToStep(0);
      }
    } catch (error) {
      setImportWarning(error?.response?.data?.error || t('contractBuilder.importError'));
    } finally {
      setImportLoading(false);
    }
  };

  // Wspólne dla pobrania i podglądu: wygeneruj Test PDF i zwróć blob URL (application/pdf).
  const fetchPreviewPdfBlobUrl = async () => {
    const contractContent = currentState.sections.map(section => {
      const replacedContent = replaceVariables(section.content);
      return `${section.title}\n${replacedContent}`;
    }).join('\n\n');

    const response = await axios.post(
      `${API_BASE}/api/preview-contract-pdf`,
      {
        content: contractContent,
        previewData: daneDokumentu,
        sections: currentState.sections,
        equipment: daneDokumentu.equipment,
        styleOptions: {
          fontSizePx: previewFontSizePx,
          tableFontPx: previewTableFontPx,
          logoAlign: normalizeLogoAlign(previewData.organizationLogoAlign),
          logoHeightPx: clampLogoHeight(previewData.organizationLogoHeightPx)
        }
      },
      { withCredentials: true, responseType: 'blob', headers: { 'X-CSRF-Token': getCsrfToken() } }
    );
    return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  };

  // Zdanie do komunikatu o nieudanym PDF: wygasła sesja jak przy zapisie, odmowa serwera (4xx)
  // jego zdaniem, awaria (5xx, sieć) dawnym komunikatem.
  const opisBleduPdf = async (err) => {
    const odp = err && err.response;
    const dane = await daneBleduPdf(odp && odp.data);
    const kod = dane && dane.error;
    if (odp && (odp.status === 401 || (odp.status === 403 && kod === 'csrf_invalid'))) return t('contractBuilder.sessionExpired');
    if (odp && odp.status >= 400 && odp.status < 500 && dane && (dane.error || dane.message)) {
      return String(dane.error || dane.message);
    }
    return (err && err.message) || '';
  };

  const handleDownloadPreview = async () => {
    try {
      setActionLoading('download');
      const url = await fetchPreviewPdfBlobUrl();
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'umowa_wypozyczenia_test.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      dispatch({ type: 'SET_MESSAGE', payload: '' });
    } catch (err) {
      const komunikat = t('contractBuilder.pdfDownloadError').replace('{msg}', await opisBleduPdf(err));
      dispatch({ type: 'SET_MESSAGE', payload: komunikat, tone: 'danger' });
      toast.error(komunikat);
    } finally {
      setActionLoading(null);
    }
  };

  // Podgląd PDF w aplikacji (modal z iframe) — pokazuje realne strony (1:1 jak wyjdzie).
  const handleViewPdf = async () => {
    try {
      setActionLoading('view-pdf');
      const url = await fetchPreviewPdfBlobUrl();
      setPdfPreviewUrl(url);
      dispatch({ type: 'SET_MESSAGE', payload: '' });
    } catch (err) {
      const komunikat = t('contractBuilder.pdfPreviewError').replace('{msg}', await opisBleduPdf(err));
      dispatch({ type: 'SET_MESSAGE', payload: komunikat, tone: 'danger' });
      toast.error(komunikat);
    } finally {
      setActionLoading(null);
    }
  };

  // Esc, „×”, fokus w oknie i jego powrót obsługują same okna z builderChrome (11.10.2026).
  const closePdfPreview = () => setPdfPreviewUrl(null);

  // Zwolnij blob URL podglądu PDF: przy podmianie, zamknięciu ORAZ odmontowaniu komponentu
  // (np. nawigacja z otwartym modalem) — inaczej URL wisi do przeładowania strony.
  useEffect(() => () => {
    if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
  }, [pdfPreviewUrl]);

  const insertVariable = (variable, sectionId) => {
    // Edytor tylko do odczytu nadal przyjmuje polecenia insertContent (test odbiorczy 11.10.2026).
    if (edycjaZablokowana) return;
    const section = currentState.sections.find(s => s.id === sectionId);
    if (!section) return;
    const name = String(variable).replace(/[{}]/g, '');
    const editor = sectionEditorsRef.current[sectionId];

    // Każdy blok (nagłówek i body) ma edytor TipTap → wstaw W MIEJSCU KURSORA
    // (naprawia stary bug doklejania na koniec). Tabela sprzętu tylko w body, nie w nagłówku.
    if (editor) {
      if (name === 'equipmentTable' && section.type !== 'heading') {
        editor.chain().focus().insertContent({ type: 'equipmentBlock' }).run();
      } else if (name === 'companyLogo') {
        // Logo jako blok — tylko w sekcjach body (w nagłówku nie renderujemy).
        if (section.type !== 'heading') {
          editor.chain().focus().insertContent({ type: 'logoBlock' }).run();
        }
      } else {
        editor.chain().focus().insertContent({ type: 'variablePill', attrs: { name } }).run();
      }
      return;
    }

    // Fallback (edytor nie zamontowany): doklej token na koniec.
    const updates = section.type === 'heading'
      ? { content: `${section.content || ''}${variable}` }
      : { content: `${section.content || ''}${variable}`, format: 'html' };
    dispatch({ type: 'UPDATE_SECTION', payload: { id: sectionId, updates } });
  };

  const getRecommendedVariablesForSection = (section) => {
    const title = (section.title || '').toLowerCase();
    const content = (section.content || '').toLowerCase();

    if (section.type === 'heading') {
      return [
        ...AVAILABLE_VARIABLES['Umowa'],
        AVAILABLE_VARIABLES['Organizacja'][0]
      ];
    }

    if (title.includes('klient') || content.includes('imi') || content.includes('nazwisko')) {
      return [
        ...AVAILABLE_VARIABLES['Dane Klienta'],
        ...AVAILABLE_VARIABLES['Umowa'].slice(0, 2)
      ];
    }

    if (title.includes('sprzęt') || title.includes('sprzet') || content.includes('equipment')) {
      return [
        ...AVAILABLE_VARIABLES['Sprzęt'],
        ...AVAILABLE_VARIABLES['Umowa'],
        ...AVAILABLE_VARIABLES['Kwoty'].slice(0, 1)
      ];
    }

    if (title.includes('finans') || title.includes('kwot') || content.includes('pln')) {
      return [
        ...AVAILABLE_VARIABLES['Kwoty'],
        ...AVAILABLE_VARIABLES['Umowa'].slice(0, 1)
      ];
    }

    if (title.includes('podpis')) {
      return [
        AVAILABLE_VARIABLES['Dane Klienta'][0],
        AVAILABLE_VARIABLES['Dane Klienta'][1],
        AVAILABLE_VARIABLES['Organizacja'][0],
        ...AVAILABLE_VARIABLES['Umowa'].slice(0, 1)
      ];
    }

    return [
      ...AVAILABLE_VARIABLES['Dane Klienta'].slice(0, 3),
      ...AVAILABLE_VARIABLES['Umowa'].slice(0, 2),
      ...AVAILABLE_VARIABLES['Kwoty'].slice(0, 2)
    ];
  };

  const getAllVariables = () => Object.values(AVAILABLE_VARIABLES).flat();
  // Zawsze widok biznesowy: chip pokazuje przyjazny opis, bez technicznego {{kodu}} (także w dymku).
  const getVariableDisplayLabel = (variable) => (variable.descKey ? t(variable.descKey) : variable.desc);

  const goToStep = (idx) => {
    setBasicStep(idx);
    setVisitedSteps((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const applyPreset = (presetKey) => {
    const preset = SECTION_PRESETS[presetKey] || SECTION_PRESETS.standard;
    setActivePreset(presetKey);
    const sections = withBasicStepId(preset.map((section, index) => ({
      ...section,
      id: index + 1
    })));

    dispatch({ type: 'REPLACE_SECTIONS', payload: sections });
    setBazaKrokow(sections);
    dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.presetApplied'), tone: 'success' });
    setVisitedSteps(new Set([basicStep]));
    setImportReport(null);
    setImportWarning('');
  };

  const applyScenario = (scenarioKey) => {
    const scenario = PREVIEW_SCENARIOS[scenarioKey];
    if (!scenario) return;
    setActiveScenario(scenarioKey);
    setPreviewData((prev) => ({
      ...prev,
      ...scenario.data
    }));
  };

  const getCsrfToken = () => {
    const fromCookie = document.cookie.split('; ').find(r => r.startsWith('csrf='))?.split('=')[1];
    return fromCookie || localStorage.getItem('csrfToken') || '';
  };

  // Przywracanie = WCZYTANIE wybranej wersji do edytora (jako roboczej). Nic nie publikuje
  // i NIE tworzy nowej wersji — użytkownik sprawdza i świadomie klika „Publikuj", by uaktywnić.
  // Czysto frontowe: pełne `sections` każdej wersji są już w templateVersions (z GET).
  const handleRestoreVersion = async (versionNo) => {
    if (edycjaZablokowana) return;
    const version = templateVersions.find((v) => v?.versionNo === versionNo);
    if (!version || !Array.isArray(version.sections)) {
      dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.versionLoadFailed').replace('{n}', String(versionNo)), tone: 'danger' });
      return;
    }
    const ok = await confirm({
      title: t('contractBuilder.versionLoadTitle').replace('{n}', String(versionNo)),
      message: t('contractBuilder.versionLoadBody'),
    });
    if (!ok) return;
    dispatch({ type: 'REPLACE_SECTIONS', payload: withBasicStepId(version.sections) });
    // Wersja to treść RAZEM z pismem: umowy z niej szły z jej rozmiarem pisma, a przywrócenie
    // brało samą treść, więc po publikacji wychodził inny dokument niż ten z historii (11.10.2026).
    if (version.previewData && typeof version.previewData === 'object') {
      const pismo = pismoZPreviewData(version.previewData);
      setPreviewFontSizePx(pismo.font);
      setPreviewTableFontPx(pismo.table);
    }
    setShowVersionHistory(false);
    dispatch({ type: 'SET_SAVED', payload: t('contractBuilder.versionLoaded').replace('{n}', String(versionNo)) });
    setTimeout(() => dispatch({ type: 'SET_MESSAGE', payload: '' }), 4000);
  };

  // Usunięcie wersji z historii (trwałe, po stronie serwera). Idzie w tej samej kolejce co zapisy
  // szkicu: serwer usuwa wersję zapisem warunkowym, więc autozapis z tej karty wysłany w tym samym
  // momencie dawał 409 jak zmiana z innej karty (przegląd kodu 12.10.2026).
  const handleDeleteVersion = async (versionNo) => {
    if (edycjaZablokowana) return;
    if (kontoZmienione()) {
      dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.accountChanged'), tone: 'danger' });
      return;
    }
    const ok = await confirm({
      title: t('contractBuilder.versionDeleteTitle').replace('{n}', String(versionNo)),
      message: t('contractBuilder.irreversible'),
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingVersion(versionNo);
    const wynik = kolejkaZapisuRef.current.then(() => usunWersje(versionNo));
    kolejkaZapisuRef.current = wynik.catch(() => {});
    await wynik;
  };
  const usunWersje = async (versionNo) => {
    try {
      const response = await axios.post(
        `${API_BASE}/api/contract-template-sections/delete-version`,
        { versionNo },
        { withCredentials: true, headers: { 'X-CSRF-Token': getCsrfToken() } }
      );
      if (Array.isArray(response?.data?.versions)) {
        setTemplateVersions(response.data.versions);
      } else {
        setTemplateVersions((prev) => prev.filter((v) => v?.versionNo !== versionNo));
      }
      dispatch({ type: 'SET_SAVED', payload: t('contractBuilder.versionDeleted').replace('{n}', String(versionNo)) });
      setTimeout(() => dispatch({ type: 'SET_MESSAGE', payload: '' }), 3000);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.code === 'template_changed') {
        setTemplateConflict(true);
        dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.templateConflict'), tone: 'danger' });
      } else {
        dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.deleteError').replace('{msg}', err.response?.data?.error || err.message), tone: 'danger' });
      }
    } finally {
      setDeletingVersion(null);
    }
  };

  const handleDragStart = (e, sectionId) => {
    setDraggedId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, sectionId) => {
    e.preventDefault();
    dragOverIdRef.current = sectionId;
  };

  const handleDrop = (e, sectionId) => {
    e.preventDefault();
    if (draggedId !== null && draggedId !== sectionId) {
      dispatch({ type: 'MOVE_SECTION_TO', payload: { fromId: draggedId, toId: sectionId } });
    }
    setDraggedId(null);
    setDragHandleActiveId(null);
    dragOverIdRef.current = null;
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragHandleActiveId(null);
    dragOverIdRef.current = null;
  };

  const handleAddSection = () => {
    const stepId = BASIC_STEPS[basicStep]?.id || 'signature-extra';
    // W widoku zaawansowanym przycisk stoi pod ostatnią sekcją, więc nowa sekcja trafia na
    // KONIEC; dawniej szła do kroku widoku prostego (domyślnie 1.), w środek umowy, poza ekran.
    dispatch({ type: 'ADD_SECTION', payload: editorMode === 'basic' ? { basicStepId: stepId } : { basicStepId: 'signature-extra', append: true } });

    if (editorMode === 'basic') {
      dispatch({ type: 'SET_MESSAGE', payload: t('contractBuilder.sectionAdded'), tone: 'success' });
    }
  };

  const handleRemoveSection = async (section) => {
    const sectionTitle = (section.title || '').trim() || `#${section.id}`;
    const confirmed = await confirm({ title: t('contractBuilder.deleteSectionTitle').replace('{name}', sectionTitle), tone: 'danger' });

    if (!confirmed) {
      return;
    }

    dispatch({ type: 'REMOVE_SECTION', payload: section.id });
  };

  const allVariableCodes = useMemo(() => new Set(getAllVariables().map(v => v.code)), []);

  const getSectionUnknownVars = (section) => {
    const used = (section.content || '').match(/{{\w+}}/g) || [];
    return [...new Set(used)].filter(v => !allVariableCodes.has(v));
  };

  // Odleglosc Levenshteina — do podpowiadania najblizszej znanej zmiennej przy imporcie
  const levenshtein = (a, b) => {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    let prev = Array.from({ length: n + 1 }, (_, j) => j);
    for (let i = 1; i <= m; i++) {
      const curr = [i];
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      prev = curr;
    }
    return prev[n];
  };

  // Polskie aliasy tokenow importu -> kody zmiennych (lustro tokenMap z backendu + semantyczne odpowiedniki)
  const POLISH_TOKEN_ALIASES = {
    imie: '{{firstName}}',
    nazwisko: '{{lastName}}',
    imie_nazwisko: '{{firstName}}',
    email: '{{email}}',
    mail: '{{email}}',
    telefon: '{{phone}}',
    tel: '{{phone}}',
    adres: '{{address}}',
    dowod: '{{idNumber}}',
    nr_dowodu: '{{idNumber}}',
    pesel: '{{idNumber}}',
    data_od: '{{startDate}}',
    data_do: '{{endDate}}',
    dni: '{{days}}',
    sprzet: '{{equipmentTable}}',
    cena: '{{totalPrice}}',
    cena_calkowita: '{{totalPrice}}',
    kaucja: '{{deposit}}',
    rabat: '{{discount}}',
    zaliczka: '{{advanceAmount}}',
    do_zaplaty: '{{amountDue}}',
    firma: '{{organizationName}}',
    nip: '{{organizationNIP}}',
    konto: '{{bankAccount}}',
    nr_konta: '{{bankAccount}}',
    notatki: '{{notes}}',
    uwagi: '{{notes}}'
  };

  const suggestKnownVariable = (unknownCode) => {
    const name = unknownCode.replace(/[{}]/g, '').toLowerCase();
    const candidates = getAllVariables();
    const byCode = (code) => candidates.find((v) => v.code === code) || null;

    // 1. Dokladny polski alias (np. pesel -> {{idNumber}})
    if (POLISH_TOKEN_ALIASES[name]) {
      return byCode(POLISH_TOKEN_ALIASES[name]);
    }

    // 2. Literowka w polskim aliasie (np. teleffon -> telefon -> {{phone}})
    let bestAlias = null;
    let bestAliasDist = Infinity;
    for (const alias of Object.keys(POLISH_TOKEN_ALIASES)) {
      const d = levenshtein(name, alias);
      if (d < bestAliasDist) {
        bestAliasDist = d;
        bestAlias = alias;
      }
    }
    if (bestAlias && bestAliasDist <= Math.max(1, Math.floor(name.length / 4))) {
      return byCode(POLISH_TOKEN_ALIASES[bestAlias]);
    }

    // 3. Dopasowanie do angielskich kodow zmiennych (np. firstnam -> firstName)
    const prefixHit = candidates.find((v) => {
      const code = v.code.replace(/[{}]/g, '').toLowerCase();
      return code.startsWith(name) || name.startsWith(code);
    });
    if (prefixHit) return prefixHit;

    let best = null;
    let bestDist = Infinity;
    for (const v of candidates) {
      const code = v.code.replace(/[{}]/g, '').toLowerCase();
      const d = levenshtein(name, code);
      if (d < bestDist) {
        bestDist = d;
        best = v;
      }
    }
    return bestDist <= Math.max(2, Math.floor(name.length / 3)) ? best : null;
  };

  const getSectionsForBasicStep = (sections, stepIndex) => {
    const step = BASIC_STEPS[stepIndex];
    return sections.filter((section) => {
      const sectionStepId = section.basicStepId || inferBasicStepId(section);
      return sectionStepId === step.id;
    });
  };

  const sectionsForCurrentStep = getSectionsForBasicStep(currentState.sections, basicStep);
  const sectionsToRender = editorMode === 'expert' ? currentState.sections : sectionsForCurrentStep;
  // Lustro serwera (lib/contractHtml.js): blok tabeli z edytora to <div data-equipment-table>,
  // a nie tekst {{equipmentTable}}, więc po edycji sekcji „Sprzęt” podgląd doklejał drugą
  // tabelę, której PDF nie ma (test odbiorczy 11.10.2026).
  const hasEquipmentPlaceholder = currentState.sections.some((section) => {
    if (section.type === 'heading') return false;
    const c = String(section.content || '');
    return /{{\s*equipmentTable\s*}}/.test(c) || /data-equipment-table/i.test(c);
  });
  // Czy logo wstawiono jako blok w którejś sekcji → wtedy NIE renderujemy go też na górze.
  const hasLogoPlaceholder = currentState.sections.some((section) => /data-company-logo|{{\s*companyLogo\s*}}/i.test(String(section.content || '')));

  // Warunki, bez których serwer ODRZUCA publikację (routes/contracts.js, POST
  // /api/contract-template-sections): blok nagłówka, jakakolwiek treść i sekcja podpisów, a nieznane
  // pola odrzuca przy każdym zapisie. Okno publikacji mówiło „Brak ostrzeżeń”, a serwer odpowiadał
  // 400, i pozwalało kliknąć „Tak, publikuj” mimo ostrzeżeń (ponowny test odbiorczy 11.10.2026).
  const blokujacePublikacje = useMemo(() => {
    const lista = [];
    const sekcje = currentState.sections;
    if (!sekcje.some((section) => section.type === 'heading')) {
      lista.push(t('contractBuilder.warnNoHeading'));
    }
    if (!sekcje.some((section) => String(section.content || '').trim().length > 0)) {
      lista.push(t('contractBuilder.warnNoContent'));
    }
    // Ta sama reguła co serwer przy publikacji (lib/contractHtml.js#looksLikeSignatureBlock):
    // tytuł zaczynający się od „Podpis” albo linia 8+ podkreśleń. Szersza reguła panelu mówiła
    // „Brak ostrzeżeń”, a serwer odrzucał publikację (test odbiorczy 11.10.2026).
    const hasSignatureSection = sekcje.some((section) => (
      /^\s*podpis/i.test(String(section.title || '')) || /_{8,}/.test(String(section.content || ''))
    ));
    if (!hasSignatureSection) {
      lista.push(t('contractBuilder.warnNoSignatures'));
    }
    const usedVariables = new Set(sekcje.flatMap((section) => String(section.content || '').match(/{{\w+}}/g) || []));
    const supportedVariables = new Set(getAllVariables().map((variable) => variable.code));
    const unknownVariables = Array.from(usedVariables).filter((variable) => !supportedVariables.has(variable));
    if (unknownVariables.length > 0) {
      lista.push(t('contractBuilder.warnUnknownVars').replace('{list}', unknownVariables.join(', ')));
    }
    return lista;
  }, [currentState.sections, t]);

  // Ostrzeżenia, które publikacji nie blokują: pusta treść sekcji (pusty tytuł jest dozwolony).
  const informacyjneOstrzezenia = useMemo(() => currentState.sections
    .filter((section) => section.type !== 'heading' && !String(section.content || '').trim())
    .map((section) => t('contractBuilder.warnEmptySection').replace('{name}', section.title || `#${section.id}`)),
  [currentState.sections, t]);

  const templateWarnings = useMemo(
    () => [...blokujacePublikacje, ...informacyjneOstrzezenia],
    [blokujacePublikacje, informacyjneOstrzezenia]
  );

  // Lista do okna publikacji: ostrzeżenia, które nie blokują, i braki danych firmy.
  const publishChecklist = useMemo(() => [
    ...informacyjneOstrzezenia,
    ...(missingCompanyFields.length > 0
      ? [t('contractBuilder.warnCompanyIncomplete').replace('{list}', missingCompanyFields.map(m => m.label).join(', '))]
      : [])
  ], [informacyjneOstrzezenia, missingCompanyFields, t]);

  const otworzOknoPublikacji = () => {
    setPublishError(null);
    setShowPublishConfirm(true);
  };
  // W trakcie publikacji okno nie znika: zamknięcie sugerowałoby, że ją odwołano.
  const zamknijOknoPublikacji = () => {
    if (actionLoading === 'publish' || publikacjaWTokuRef.current) return;
    setShowPublishConfirm(false);
  };
  // Stan „publikuję” od KLIKNIĘCIA, nie od chwili, gdy publikacja wyjdzie z kolejki zapisów: gdy
  // przed nią czekał autozapis, „Tak, publikuj” było nadal aktywne, a okno dało się zamknąć, więc
  // drugi klik publikował drugą wersję (przegląd kodu 12.10.2026). Ref łapie też dwa kliknięcia
  // w tej samej klatce, zanim przycisk zdąży się wyłączyć.
  const publikacjaWTokuRef = useRef(false);
  const potwierdzPublikacje = async () => {
    if (blokujacePublikacje.length > 0 || publikacjaWTokuRef.current) return;
    publikacjaWTokuRef.current = true;
    setPublishError(null);
    setActionLoading('publish');
    try {
      const wynik = await handleSaveTemplate('publish');
      if (wynik && wynik.ok) setShowPublishConfirm(false);
      else if (wynik && wynik.error) setPublishError(wynik.error);
    } finally {
      publikacjaWTokuRef.current = false;
      // Zapis, który wrócił bez wysłania (np. konflikt), nie zdejmuje stanu sam.
      setActionLoading((a) => (a === 'publish' ? null : a));
    }
  };

  // Postep krokow w trybie prostym: ktore kroki odwiedzono i ktore zmodyfikowano wzgledem presetu
  const stepProgress = useMemo(() => {
    const baseline = bazaKrokow;
    const serializeStep = (sections, stepId) => sections
      .filter((s) => (s.basicStepId || inferBasicStepId(s)) === stepId)
      .map((s) => `${s.title}\u0000${s.content}\u0000${s.alignment || 'left'}`)
      .join('\u0001');

    return BASIC_STEPS.map((step, idx) => ({
      modified: serializeStep(currentState.sections, step.id) !== serializeStep(baseline, step.id),
      visited: visitedSteps.has(idx)
    }));
  }, [currentState.sections, bazaKrokow, visitedSteps]);

  // Status szablonu: kolor niesie odznaka, tresc — jedno zdanie obok. Wczesniej byl to
  // pelnej szerokosci baner w kolorze stanu, ktory dominowal nad cala strona.
  // Szkic różny od wersji opublikowanej NIE jest „Opublikowany”: serwer zwraca status
  // 'published', gdy istnieje jakakolwiek wersja opublikowana, a edytor pokazuje szkic, więc
  // firma widziała „Nowe umowy będą używać tego szablonu” przy treści, której klienci nie
  // dostają (test odbiorczy 11.10.2026). Różnica samego pisma też się liczy, bo umowy dostają
  // pismo wersji opublikowanej (ponowny test 11.10.2026).
  const szkicPonadOpublikowany = templateStatus === 'published' && publishedSnapshot !== null
    && (JSON.stringify(currentState.sections) !== publishedSnapshot
      || (publishedInfo !== null
        && (previewFontSizePx !== publishedInfo.font || previewTableFontPx !== publishedInfo.table)));
  // Nieudany odczyt szablonu: stan nie jest znany, więc odznaka nie może mówić „Brak szablonu”,
  // gdy firma ma opublikowaną wersję (test odbiorczy 11.10.2026).
  const statusTone = templateLoadFailed
    ? 'danger'
    : templateStatus === 'published' && !szkicPonadOpublikowany
      ? 'success'
      : templateStatus === 'empty' ? 'info' : 'warning';
  const statusLabel = templateLoadFailed
    ? t('contractBuilder.statusLoadFailed')
    : szkicPonadOpublikowany
    ? t('contractBuilder.statusDraftOverPublished').replace('{n}', String(publishedVersion || ''))
    : templateStatus === 'published'
      ? (publishedVersion ? t('contractBuilder.statusPublishedVersion').replace('{n}', String(publishedVersion)) : t('contractBuilder.statusPublished'))
      : templateStatus === 'draft' ? t('contractBuilder.statusDraft') : t('contractBuilder.statusEmpty');
  const statusDescription = templateLoadFailed
    ? t('contractBuilder.statusDescLoadFailed')
    : szkicPonadOpublikowany
    ? t('contractBuilder.statusDescDraftOverPublished').replace('{n}', String(publishedVersion || ''))
    : templateStatus === 'published'
      ? t('contractBuilder.statusDescPublished')
      : templateStatus === 'draft'
        ? t('contractBuilder.statusDescDraft')
        : t('contractBuilder.statusDescEmpty');

  return (
    <Container>
      {/* Pasek akcji zostaje na ekranie przy przewijaniu — zapis i publikacja dotycza
          calego dokumentu, wiec nie moga byc schowane pod lista sekcji. */}
      <TopBar>
        <TitleBlock>
          <PageTitle>{t('contractBuilder.pageTitle')}</PageTitle>
          <Badge $tone={statusTone}>{statusLabel}</Badge>
        </TitleBlock>

        <TopActions>
          {autoSaveStatus === 'saving' && (
            <AutoSaveIndicator>{t('common.saving')}</AutoSaveIndicator>
          )}
          {autoSaveStatus === 'saved' && lastAutoSaveAt && (
            <AutoSaveIndicator>
              {t('contractBuilder.autoSavedAt').replace('{time}', lastAutoSaveAt.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }))}
            </AutoSaveIndicator>
          )}
          {autoSaveStatus === 'error' && (
            <AutoSaveIndicator error>{t('contractBuilder.autoSaveError')}</AutoSaveIndicator>
          )}
          {!tylkoOdczyt && (
            <>
              <Button onClick={() => handleSaveTemplate('save-draft')} disabled={actionLoading !== null || templateConflict || !szablonWczytany}>
                <SaveIcon size={16} /> {actionLoading === 'save-draft' ? t('common.saving') : t('contractBuilder.saveDraft')}
              </Button>
              <PublishButton
                onClick={otworzOknoPublikacji}
                disabled={actionLoading !== null || templateConflict || !szablonWczytany}
              >
                <UploadIcon size={16} /> {actionLoading === 'publish' ? t('contractBuilder.publishing') : t('contractBuilder.publishTemplate')}
              </PublishButton>
            </>
          )}
        </TopActions>
      </TopBar>

      <Intro>
        {t('contractBuilder.intro')}
      </Intro>

      <StatusStrip>
        <StatusText>{statusDescription}</StatusText>
        {templateStatus !== 'empty' && (
          <SmallButton onClick={() => setShowVersionHistory(v => !v)}>
            {t('contractBuilder.history').replace('{count}', String(templateVersions.length))}
          </SmallButton>
        )}
        <SmallButton onClick={() => navigate('/contract-settings')}>
          {t('contractBuilder.contractSettings')}
        </SmallButton>
      </StatusStrip>

      <Notices>
        {currentState.message && (
          <Banner tone={currentState.messageTone || 'info'}>
            {currentState.message}
          </Banner>
        )}

        {missingCompanyFields.length > 0 && (
          <Banner
            tone="warning"
            title={t('contractBuilder.bannerIncomplete')}
            action={{ label: t('contractBuilder.bannerFillCompany'), onClick: () => navigate('/contract-settings') }}
          >
            {t('contractBuilder.missingLabel')} <strong>{missingCompanyFields.map(m => m.label).join(', ')}</strong>.
            {' '}{t('contractBuilder.missingCompanyEffect')}
          </Banner>
        )}

        {templateLoadFailed && (
          <Banner tone="danger" title={t('contractBuilder.templateLoadFailed')} />
        )}

        {templateConflict && (
          <Banner tone="danger" title={t('contractBuilder.templateConflict')} data-cy="contract-template-conflict" />
        )}

        {tylkoOdczyt && (
          <Banner tone="info" title={wPrzerwie ? (powodOdczytu === 'rezygnacja' ? t('rezygnacja.szablonTylkoOdczyt') : t('przerwa.szablonTylkoOdczyt')) : t('contractBuilder.readOnlyNotice')} />
        )}

        {settingsLoadFailed && (
          <Banner
            tone="danger"
            title={t('contractBuilder.bannerLoadFailed')}
            action={{ label: t('contractBuilder.bannerSettingsAction'), onClick: () => navigate('/contract-settings') }}
          >
            {t('contractBuilder.bannerLoadFailedBody')}
          </Banner>
        )}
      </Notices>

      {showVersionHistory && templateVersions.length > 0 && (
        <HistoryBox data-cy="contract-history">
          <HistoryHead>
            <span>{t('contractBuilder.historyHead').replace('{count}', String(templateVersions.length))}</span>
            <SmallButton onClick={() => setShowVersionHistory(false)}>{t('common.close')}</SmallButton>
          </HistoryHead>
          {templateVersions.map((ver) => (
            <VersionCard key={ver.versionNo}>
              <div>
                <strong>v{ver.versionNo}</strong>
                {ver.publishedAt && (
                  <VersionDate>{new Date(ver.publishedAt).toLocaleString('pl-PL')}</VersionDate>
                )}
              </div>
              {!edycjaZablokowana && (
                <ChipRow>
                  <SmallButton onClick={() => handleRestoreVersion(ver.versionNo)} title={t('contractBuilder.restoreTitle')}>
                    {t('contractBuilder.restore')}
                  </SmallButton>
                  <SmallButton
                    onClick={() => handleDeleteVersion(ver.versionNo)}
                    disabled={deletingVersion !== null}
                    style={{ color: 'var(--danger)' }}
                    title={t('contractBuilder.deleteVersionTitle')}
                  >
                    {deletingVersion === ver.versionNo ? t('contractBuilder.deleting') : t('common.delete')}
                  </SmallButton>
                </ChipRow>
              )}
            </VersionCard>
          ))}
        </HistoryBox>
      )}

      <MainGrid>
        {/* ── PANEL EDYCJI ── (dla pracownika treść sekcji jest zablokowana fieldsetem niżej) */}
        <Panel>
          <PanelHead>
            <PanelTitle>{t('contractBuilder.contentPanel')}</PanelTitle>
            <PanelTools>
              <Button
                onClick={() => setEditorMode(m => m === 'basic' ? 'expert' : 'basic')}
                title={t('contractBuilder.advancedToggle')}
              >
                {editorMode === 'basic' ? t('contractBuilder.modeAdvanced') : t('contractBuilder.modeSimple')}
              </Button>
              <IconBtn
                onClick={() => dispatch({ type: 'UNDO' })}
                disabled={edycjaZablokowana || state.historyIndex === 0}
                title={t('contractBuilder.undoTitle')}
                aria-label={t('common.undo')}
              >
                <UndoIcon />
              </IconBtn>
              <IconBtn
                onClick={() => dispatch({ type: 'REDO' })}
                disabled={edycjaZablokowana || state.historyIndex === state.history.length - 1}
                title={t('contractBuilder.redoTitle')}
                aria-label={t('contractBuilder.redo')}
              >
                <RedoIcon />
              </IconBtn>
            </PanelTools>
          </PanelHead>

          {editorMode === 'basic' && (
            <>
              {/* Wzory i import to JEDNA decyzja („skad wziac tresc"), wiec jedna karta.
                  Przy pustym szablonie karta jest wyrozniona, bo wtedy jest jedyna sensowna
                  akcja na stronie. */}
              {/* Wzory i import podmieniają treść, więc pracownik bez uprawnień ich nie widzi. */}
              {!edycjaZablokowana && (
              <StartCard $highlight={templateStatus === 'empty'} data-tour="contract-start">
                <StartTitle>{t('contractBuilder.startTitle')}</StartTitle>
                <StartHint>{t('contractBuilder.startHint')}</StartHint>
                <ChipRow>
                  <ToolChip $active={activePreset === 'standard'} aria-pressed={activePreset === 'standard'} onClick={() => applyPreset('standard')}>
                    Standard
                  </ToolChip>
                  <ToolChip $active={activePreset === 'withNotes'} aria-pressed={activePreset === 'withNotes'} onClick={() => applyPreset('withNotes')}>
                    {t('contractBuilder.presetStandardNotes')}
                  </ToolChip>
                  <ToolChip $active={activePreset === 'noDeposit'} aria-pressed={activePreset === 'noDeposit'} onClick={() => applyPreset('noDeposit')}>
                    {t('contractBuilder.presetNoDeposit')}
                  </ToolChip>
                </ChipRow>

                <SubBlock>
                  <Disclosure
                    $open={showImport}
                    onClick={() => setShowImport(v => !v)}
                    aria-expanded={showImport}
                  >
                    <ChevronDownIcon /> {t('contractBuilder.importDisclosure')}
                  </Disclosure>

                  {showImport && (
                    <>
                      <FieldGroup style={{ marginTop: 'var(--sp-3)' }}>
                        <Label htmlFor="import-text">{t('contractBuilder.importLabel')}</Label>
                        <TextArea
                          id="import-text"
                          value={importText}
                          onChange={(e) => setImportText(e.target.value)}
                          placeholder={t('contractBuilder.importPlaceholder')}
                        />
                      </FieldGroup>
                      <Button $primary onClick={handleImportFromText} disabled={importLoading}>
                        {importLoading ? t('contractBuilder.importing') : t('contractBuilder.importSubmit')}
                      </Button>
                      {importWarning && (
                        <HelperText style={{ margin: 'var(--sp-3) 0 0' }}>{importWarning}</HelperText>
                      )}
                      {importReport && <ImportReportList report={importReport} />}
                    </>
                  )}
                </SubBlock>
              </StartCard>
              )}

              <Stepper>
                {BASIC_STEPS.map((step, idx) => (
                  <StepButton
                    key={step.id}
                    $active={idx === basicStep}
                    aria-current={idx === basicStep ? 'step' : undefined}
                    onClick={() => goToStep(idx)}
                  >
                    <StepNum $active={idx === basicStep} $done={stepProgress[idx].modified}>
                      {stepProgress[idx].modified ? '✓' : idx + 1}
                    </StepNum>
                    {step.labelKey ? t(step.labelKey) : step.label}
                  </StepButton>
                ))}
              </Stepper>
              {/* Kroki niosą klucze tłumaczeń; ekran czytał nieistniejące `label`/`helper`, więc
                  kroki 1 i 2 nie miały nazw, a podpowiedź była pusta (test odbiorczy 11.10.2026). */}
              <HelperText>{BASIC_STEPS[basicStep].helperKey ? t(BASIC_STEPS[basicStep].helperKey) : BASIC_STEPS[basicStep].helper}</HelperText>
            </>
          )}

          <ZablokowanaEdycja
            disabled={edycjaZablokowana}
            {...(edycjaZablokowana ? { 'data-cy': 'contract-editor-readonly' } : {})}
          >
          {sectionsToRender.map((section) => {
            const idx = currentState.sections.findIndex((item) => item.id === section.id);
            const unknownVars = getSectionUnknownVars(section);
            const isDragging = draggedId === section.id;
            const hasTitle = !!(section.title && section.title.trim());
            // 'left' na koncu tylko dla wariantu lewego — dla center/right brak ustawienia
            // i tak nie zrownuje sie z porownywana wartoscia (zachowanie sprzed redesignu).
            const align = section.titleAlignment || section.alignment || 'left';
            return (
            <Section
              key={section.id}
              // Bez przeciągania przy edycji zablokowanej (pracownik, konflikt): kolejność sekcji
              // to też zmiana szablonu (12.10.2026).
              draggable={!edycjaZablokowana && dragHandleActiveId === section.id}
              onDragStart={(e) => handleDragStart(e, section.id)}
              onDragOver={(e) => handleDragOver(e, section.id)}
              onDrop={(e) => handleDrop(e, section.id)}
              onDragEnd={handleDragEnd}
              style={{ opacity: isDragging ? 0.5 : 1, transition: 'opacity 0.15s' }}
            >
              <SectionHead>
                <DragHandle
                  title={t('contractBuilder.dragToReorder')}
                  onMouseDown={() => setDragHandleActiveId(section.id)}
                  onMouseUp={() => setDragHandleActiveId(null)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="9" cy="5" r="2"/><circle cx="15" cy="5" r="2"/>
                    <circle cx="9" cy="12" r="2"/><circle cx="15" cy="12" r="2"/>
                    <circle cx="9" cy="19" r="2"/><circle cx="15" cy="19" r="2"/>
                  </svg>
                </DragHandle>
                <SectionIndex aria-hidden="true">{idx + 1}</SectionIndex>
                <SectionTitle $muted={!hasTitle}>
                  {hasTitle ? section.title : t('contractBuilder.untitledInContract')}
                </SectionTitle>
                {unknownVars.length > 0 && (
                  <SectionWarningBadge title={t('contractBuilder.unknownVarsTitle').replace('{list}', unknownVars.join(', '))}>
                    ⚠ {t('contractBuilder.unknownVarsBadge').replace('{n}', String(unknownVars.length))}
                  </SectionWarningBadge>
                )}
                {editorMode === 'expert' && (
                  <SectionActions>
                    <IconBtn
                      onClick={() => dispatch({ type: 'MOVE_SECTION_UP', payload: section.id })}
                      disabled={idx === 0}
                      title={t('contractBuilder.moveUp')}
                      aria-label={t('contractBuilder.moveUp')}
                    >
                      <MoveUpIcon />
                    </IconBtn>
                    <IconBtn
                      onClick={() => dispatch({ type: 'MOVE_SECTION_DOWN', payload: section.id })}
                      disabled={idx === currentState.sections.length - 1}
                      title={t('contractBuilder.moveDown')}
                      aria-label={t('contractBuilder.moveDown')}
                    >
                      <MoveDownIcon />
                    </IconBtn>
                  </SectionActions>
                )}
              </SectionHead>

              <FieldGroup>
                <FieldHead>
                  {/* W bloku tytułu do dokumentu idzie wyłącznie „Treść bloku”, a to pole jest nazwą bloku
                      w edytorze. Ta sama etykieta co w sekcji („Nagłówek bloku”, który się drukuje)
                      sugerowała, że zmienia tytuł umowy (test odbiorczy 11.10.2026). */}
                  <Label htmlFor={`section-title-${section.id}`}>
                    {section.type === 'heading' ? t('contractBuilder.blockNameHeading') : t('contractBuilder.blockHeading')}
                  </Label>
                  {section.type === 'section' && (
                    <Segmented role="group" aria-label={t('contractBuilder.headingAlignGroup')}>
                      <SegButton
                        $active={align === 'left'}
                        onClick={() => dispatch({ type: 'UPDATE_SECTION', payload: { id: section.id, updates: { titleAlignment: 'left' } } })}
                        title={t('contractBuilder.headingLeft')}
                        aria-label={t('contractBuilder.headingLeft')}
                      ><AlignLeftIcon /></SegButton>
                      <SegButton
                        $active={align === 'center'}
                        onClick={() => dispatch({ type: 'UPDATE_SECTION', payload: { id: section.id, updates: { titleAlignment: 'center' } } })}
                        title={t('contractBuilder.headingCenter')}
                        aria-label={t('contractBuilder.headingCenter')}
                      ><AlignCenterIcon /></SegButton>
                      <SegButton
                        $active={align === 'right'}
                        onClick={() => dispatch({ type: 'UPDATE_SECTION', payload: { id: section.id, updates: { titleAlignment: 'right' } } })}
                        title={t('contractBuilder.headingRight')}
                        aria-label={t('contractBuilder.headingRight')}
                      ><AlignRightIcon /></SegButton>
                    </Segmented>
                  )}
                </FieldHead>
                <Input
                  id={`section-title-${section.id}`}
                  value={section.title}
                  aria-describedby={section.type === 'heading' ? `section-title-hint-${section.id}` : undefined}
                  onChange={(e) => dispatch({
                    type: 'UPDATE_SECTION',
                    payload: { id: section.id, updates: { title: e.target.value } }
                  })}
                />
                {section.type === 'heading' && (
                  <SettingHint id={`section-title-hint-${section.id}`}>{t('contractBuilder.blockNameHeadingHint')}</SettingHint>
                )}
              </FieldGroup>

              <FieldGroup>
                <Label>{t('contractBuilder.blockContent')}</Label>
                <SectionEditor
                  sectionId={section.id}
                  value={section.content}
                  format={section.format}
                  labelFor={labelForVariable}
                  registerEditor={registerSectionEditor}
                  readOnly={edycjaZablokowana}
                  onChange={(html) => dispatch({
                    type: 'UPDATE_SECTION',
                    payload: { id: section.id, updates: { content: html, format: 'html' } }
                  })}
                />
              </FieldGroup>

              <FieldHead style={{ justifyContent: 'space-between', marginBottom: 'var(--sp-2)' }}>
                <Label>{t('contractBuilder.varsLabel')}</Label>
                <SmallButton
                  onClick={() => setShowAllVariablesBySection((prev) => ({
                    ...prev,
                    [section.id]: !prev[section.id]
                  }))}
                >
                  {showAllVariablesBySection[section.id] ? t('contractBuilder.showLess') : t('contractBuilder.showAllFields')}
                </SmallButton>
              </FieldHead>

              <ChipRow>
                {(showAllVariablesBySection[section.id] ? getAllVariables() : getRecommendedVariablesForSection(section)).map((v) => (
                  <VariableButton key={v.code} onClick={() => insertVariable(v.code, section.id)}>
                    {getVariableDisplayLabel(v)}
                  </VariableButton>
                ))}
              </ChipRow>

              {(currentState.sections.length > 2 && (editorMode === 'expert' || section.isCustom)) && (
                <DangerGhostButton
                  onClick={() => handleRemoveSection(section)}
                  style={{ marginTop: 'var(--sp-3)' }}
                >
                  <TrashIcon size={14} /> {t('contractBuilder.deleteSection')}
                </DangerGhostButton>
              )}
            </Section>
          );
          })}

          <Button onClick={handleAddSection} $primary style={{ width: '100%', marginTop: 'var(--sp-3)' }}>
            <PlusIcon /> {t('contractBuilder.addSection')}
          </Button>
          </ZablokowanaEdycja>
        </Panel>

        {/* ── PANEL PODGLĄDU ── */}
        <PreviewPanel>
          <PanelHead data-tour="contract-preview">
            <PanelTitle>{t('contractBuilder.previewPanel')}</PanelTitle>
            {/* Obie akcje PDF stoja przy podgladzie, bo dzialaja wlasnie na nim; wczesniej
                lezaly na dole strony razem z zapisem i publikacja, wiec cztery przyciski
                o roznym znaczeniu wygladaly na rownorzedne. */}
            <PanelTools>
              <Button ref={pdfPrzyciskRef} onClick={handleViewPdf} disabled={actionLoading !== null}>
                <EyeIcon size={16} /> {actionLoading === 'view-pdf' ? t('contractBuilder.generating') : t('contractBuilder.pdfPreview')}
              </Button>
              <Button onClick={handleDownloadPreview} disabled={actionLoading !== null}>
                <DownloadIcon size={16} /> {actionLoading === 'download' ? t('contractBuilder.downloading') : t('contractBuilder.downloadPdf')}
              </Button>
            </PanelTools>
          </PanelHead>

          <PreviewToolbar>
            <ToolLabel>{t('contractBuilder.testOn')}</ToolLabel>
            {Object.entries(PREVIEW_SCENARIOS).map(([key, scenario]) => (
              <ToolChip
                key={key}
                $active={activeScenario === key}
                aria-pressed={activeScenario === key}
                onClick={() => applyScenario(key)}
              >
                {t(scenario.labelKey)}
              </ToolChip>
            ))}
          </PreviewToolbar>

          <PreviewFrame>
            {/* Marginesy MUSZĄ odpowiadać wywołaniu page.pdf w
                `Login/backend/lib/contractHtml.js#renderPdfFromHtml` (16/15/16/15 mm) —
                inaczej kartka w podglądzie łamie wiersze w innym miejscu niż wydruk.
                pageFooter: PDF umowy ma na każdej stronie stopkę z nazwą firmy i numerem strony
                (buildPdfFooterTemplate z `footerLabel` = nazwa firmy), więc kartka też, jak
                w konstruktorze oferty (przegląd kodu 12.10.2026). */}
            <PaperSheet
              marginTopMm={16}
              marginSideMm={15}
              marginBottomMm={16}
              onPagesChange={setPreviewPages}
              pageFooter={String(previewData.organizationName || '').trim()}
            >
              <PreviewContainer $fontSizePx={previewFontSizePx}>
                {!hasLogoPlaceholder && previewData.organizationLogoUrl && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent:
                        normalizeLogoAlign(previewData.organizationLogoAlign) === 'left'
                          ? 'flex-start'
                          : normalizeLogoAlign(previewData.organizationLogoAlign) === 'right'
                            ? 'flex-end'
                            : 'center',
                      marginBottom: '18px'
                    }}
                  >
                    <img
                      src={previewData.organizationLogoUrl}
                      alt="Logo organizacji"
                      style={{
                        maxHeight: `${clampLogoHeight(previewData.organizationLogoHeightPx)}px`,
                        maxWidth: '260px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
                {renderPreview()}
                {previewData.equipment && previewData.equipment.length > 0 && !hasEquipmentPlaceholder && (
                  <>
                    <div style={{ marginTop: '16px', fontWeight: 'bold' }}>Sprzęt do wypożyczenia:</div>
                    {renderEquipmentTable()}
                  </>
                )}
              </PreviewContainer>
            </PaperSheet>
          </PreviewFrame>
          <SheetNote>
            A4 · {pageCountLabel(previewPages)}
            {previewPages > 1 && ` · ${t('contractBuilder.sheetSplitNote')}`}
          </SheetNote>

          {templateWarnings.length > 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Banner tone="warning" title={`${t('contractBuilder.checkBeforePublish')} (${templateWarnings.length})`}>
                <ul style={{ margin: 'var(--sp-1) 0 0 18px', padding: 0 }}>
                  {templateWarnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Banner>
            </div>
          )}

          <SubBlock>
            <ToolLabel>{t('contractBuilder.documentLook')}</ToolLabel>
            <SettingRow style={{ marginTop: 'var(--sp-3)' }}>
              <Label as="span">Logo</Label>
              <SettingHint>
                {previewData.organizationLogoUrl
                  ? t('contractBuilder.logoSet')
                  : t('contractBuilder.logoMissing')}
              </SettingHint>
            </SettingRow>
            <SettingRow>
              <Label as="span">{t('contractBuilder.fontSize')}</Label>
              <SimpleSelect
                style={{ width: 100 }}
                disabled={edycjaZablokowana}
                aria-label={t('contractBuilder.fontSize')}
                value={previewFontSizePx}
                onChange={(val) => setPreviewFontSizePx(clampPreviewFontSize(val))}
                options={DOCUMENT_FONT_SIZES.map((px) => ({ value: px, label: `${px} px` }))}
              />
            </SettingRow>
            <SettingRow>
              <Label as="span">{t('contractBuilder.tableFontSize')}</Label>
              <SimpleSelect
                style={{ width: 100 }}
                disabled={edycjaZablokowana}
                aria-label={t('contractBuilder.tableFontSize')}
                value={previewTableFontPx}
                onChange={(val) => setPreviewTableFontPx(clampTableFontPx(val))}
                options={TABLE_FONT_SIZES.map((px) => ({ value: px, label: `${px} px` }))}
              />
            </SettingRow>
          </SubBlock>

          <SubBlock>
            <Disclosure
              $open={showVariablesList}
              onClick={() => setShowVariablesList(v => !v)}
              aria-expanded={showVariablesList}
            >
              <ChevronDownIcon /> {t('contractBuilder.allFields')}
            </Disclosure>
            {showVariablesList && (
              <div style={{ marginTop: 'var(--sp-3)' }}>
                {Object.entries(AVAILABLE_VARIABLES).map(([category, variables]) => (
                  <VarGroup key={category}>
                    <VarGroupLabel>{VAR_CATEGORY_KEYS[category] ? t(VAR_CATEGORY_KEYS[category]) : category}</VarGroupLabel>
                    <ChipRow>
                      {variables.map((v) => (
                        <VariableButton
                          key={v.code}
                          disabled={edycjaZablokowana}
                          onClick={() => {
                            const ostatnia = lastFocusedSectionIdRef.current;
                            const sectionId = (ostatnia && currentState.sections.some((s) => s.id === ostatnia))
                              ? ostatnia
                              : currentState.sections.find(s => s.type === 'section')?.id;
                            if (sectionId) insertVariable(v.code, sectionId);
                          }}
                        >
                          {getVariableDisplayLabel(v)}
                        </VariableButton>
                      ))}
                    </ChipRow>
                  </VarGroup>
                ))}
              </div>
            )}
          </SubBlock>
        </PreviewPanel>
      </MainGrid>

      {showPublishConfirm && (
        <ModalOverlay onClick={zamknijOknoPublikacji}>
          <ModalBox onClick={(e) => e.stopPropagation()} closeLabel={t('common.close')}>
            <ModalTitle>{t('contractBuilder.publishTitle')}</ModalTitle>
            <ModalText>
              {publishedVersion
                ? t('contractBuilder.publishBodyReplace').replace('{n}', String(publishedVersion))
                : t('contractBuilder.publishBodyFirst')}
            </ModalText>
            {blokujacePublikacje.length > 0 && (
              <Banner tone="danger" title={t('contractBuilder.publishBlocked')}>
                <ul style={{ margin: 'var(--sp-2) 0 0 0', paddingLeft: 18 }}>
                  {blokujacePublikacje.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </Banner>
            )}
            {publishChecklist.length > 0 && (
              <div style={{ marginTop: blokujacePublikacje.length > 0 ? 'var(--sp-2)' : 0 }}>
                <Banner tone="warning" title={t('contractBuilder.checkBeforePublish')}>
                  <ul style={{ margin: 'var(--sp-2) 0 0 0', paddingLeft: 18 }}>
                    {publishChecklist.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </Banner>
              </div>
            )}
            {blokujacePublikacje.length === 0 && publishChecklist.length === 0 && (
              <Banner tone="success">{t('contractBuilder.noWarnings')}</Banner>
            )}
            {publishError && (
              <div style={{ marginTop: 'var(--sp-2)' }} data-cy="contract-publish-error">
                <Banner tone="danger">{publishError}</Banner>
              </div>
            )}
            <ModalActions>
              <Button onClick={zamknijOknoPublikacji} disabled={actionLoading === 'publish'}>{t('common.cancel')}</Button>
              <PublishButton
                onClick={potwierdzPublikacje}
                disabled={blokujacePublikacje.length > 0 || actionLoading !== null}
              >
                {actionLoading === 'publish' ? t('contractBuilder.publishing') : t('contractBuilder.yesPublish')}
              </PublishButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {pdfPreviewUrl && (
        <ModalOverlay onClick={closePdfPreview} powrotFokusu={pdfPrzyciskRef}>
          <PdfModalBox onClick={(e) => e.stopPropagation()} aria-label={t('contractBuilder.pdfPreview')}>
            <PdfModalHead>
              <span>{t('contractBuilder.pdfModalHint')}</span>
              <ChipRow>
                <PdfDownloadLink href={pdfPreviewUrl} download="umowa_wypozyczenia_test.pdf">
                  {t('common.download')}
                </PdfDownloadLink>
                <PdfCloseButton onClick={closePdfPreview} />
              </ChipRow>
            </PdfModalHead>
            <PdfFrame title={t('contractBuilder.pdfFrameTitle')} src={pdfPreviewUrl} />
          </PdfModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
}
